<template>
  <div>
    <div class="mb-2 pe-4">
      <div class="d-flex align-items-center">
        <div class="color-common p-2">
          <h3 class="title-page mb-0 ps-3">APPOINTMENT CALENDAR</h3>
        </div>
      </div>
    </div>

    <div class="page-header align-items-start min-vh-100">
      <span class="mask bg-white"></span>
      <div class="container">
        <div class="row">
          <div class="col-lg-12 col-md-12 col-12 mx-auto">

            <!-- Connection status. A calendar with no events and a calendar the
                 service account cannot reach look identical, so say which it is. -->
            <div
              class="conn-strip mt-4"
              :class="connection.connected ? 'conn-ok' : 'conn-bad'"
            >
              <span class="conn-dot"></span>
              <template v-if="connection.connected">
                Connected to <strong>{{ connection.summary }}</strong>
                <span class="conn-muted">· {{ connection.timezone }} · {{ connection.client_email }}</span>
              </template>
              <template v-else>
                {{ connection.error || "Checking Google Calendar connection..." }}
              </template>
            </div>

            <!-- Google is a mirror of the booking rows, not the source of them. When it
                 is unreachable the appointments still list; only the manually-typed
                 calendar events are missing, and that is worth saying out loud. -->
            <div v-if="calendarError" class="conn-strip conn-warn mt-2">
              <span class="conn-dot"></span>
              Showing booked appointments only — Google Calendar could not be read:
              <span class="conn-muted">{{ calendarError }}</span>
            </div>

            <!-- Controls -->
            <div class="d-flex flex-wrap align-items-center mt-3 gap-3">
              <div class="d-flex align-items-center s-branch">
                <div class="w-100px">Branch</div>
                <Multiselect
                  v-model="branchId"
                  :options="branches"
                  placeholder="Please select a Branch"
                  :canClear="false"
                  @change="onChangeBranch"
                />
              </div>

              <!-- SH025 — the view decides both what the grid shows and how far one click
                   of the arrows moves. A month per click reads wrong on a day view. -->
              <div class="view-switch">
                <button
                  v-for="opt in viewOptions"
                  :key="opt.value"
                  class="view-switch-btn"
                  :class="{ 'is-on': view === opt.value }"
                  type="button"
                  @click="setView(opt.value)"
                >{{ opt.label }}</button>
              </div>

              <div class="month-nav d-flex align-items-center ms-auto">
                <button class="btn-nav" type="button" @click="shiftRange(-1)">&lsaquo;</button>
                <div class="month-label">{{ rangeLabel }}</div>
                <button class="btn-nav" type="button" @click="shiftRange(1)">&rsaquo;</button>
              </div>

              <button class="btn-plain" type="button" @click="loadAppointments">Refresh</button>
            </div>

            <div v-if="view === 'day'" class="conn-strip conn-hint mt-2">
              Click a quarter hour in a stylist's column — or drag down it — to book someone
              in or mark the stylist away.
            </div>

            <!-- Stylist filter. Hidden ids are what is stored, not shown ones: a stylist
                 added after the merchant made their choice should appear, not be silently
                 filtered out. -->
            <div class="staff-filter mt-3">
              <div class="staff-filter-head">
                <span class="staff-filter-title">Stylists</span>
                <span class="conn-muted">
                  {{ visibleStaffCount }}/{{ staffOptions.length }} shown ·
                  {{ visibleAppointments.length }} of {{ appointments.length }} appointment(s)
                </span>
                <div class="ms-auto d-flex gap-2">
                  <button class="btn-plain btn-mini" type="button" @click="showAllStaff">Show all</button>
                  <button class="btn-plain btn-mini" type="button" @click="hideAllStaff">Hide all</button>
                </div>
              </div>

              <div v-if="staffOptions.length" class="staff-chips">
                <label
                  v-for="staff in staffOptions"
                  :key="staff.staff_id"
                  class="staff-chip"
                  :class="{ 'is-off': isStaffHidden(staff.staff_id) }"
                >
                  <input
                    type="checkbox"
                    :checked="!isStaffHidden(staff.staff_id)"
                    @change="toggleStaff(staff.staff_id)"
                  />
                  <span class="staff-chip-name">{{ staff.staff_name }}</span>
                  <span class="staff-chip-count">{{ staff.total }}</span>
                </label>
              </div>
              <div v-else class="staff-empty">
                No service staff assigned to this sale channel yet.
              </div>
            </div>

            <div class="cal-layout mt-3">
              <div class="cal-main">

            <!-- Month grid -->
            <div v-if="view === 'month'" class="cal-wrap">
              <div class="cal-head">
                <div v-for="d in weekdays" :key="d" class="cal-head-cell">{{ d }}</div>
              </div>
              <div class="cal-body">
                <div
                  v-for="cell in calendarCells"
                  :key="cell.key"
                  class="cal-cell"
                  :class="{
                    'is-outside': !cell.inMonth,
                    'is-today': cell.isToday,
                    'is-selected': cell.key === selectedDate
                  }"
                  @click="selectDate(cell.key)"
                >
                  <div class="cal-daynum">{{ cell.day }}</div>
                  <div
                    v-for="ev in cell.events.slice(0, 3)"
                    :key="ev.key"
                    class="cal-chip is-clickable"
                    :title="chipTitle(ev)"
                    @click.stop="openFromMonth(cell.key, ev)"
                  >
                    <span class="cal-chip-time">{{ ev.time_text }}</span>
                    {{ ev.customer_name || ev.title }}
                  </div>
                  <div v-if="cell.events.length > 3" class="cal-more">
                    +{{ cell.events.length - 3 }} more
                  </div>
                  <!-- SH038 — who is away that day, under the appointments they are not
                       taking. -->
                  <div
                    v-for="off in cell.timeOff"
                    :key="'o' + off.id"
                    class="cal-chip cal-chip-off"
                    :title="offTitle(off)"
                  >
                    <span class="cal-chip-time">{{ off.is_all_day ? "Off" : off.time_text }}</span>
                    {{ off.staff_name }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Day and Week share one grid: a column per stylist per day, a row per
                 quarter hour. Week simply has seven times the columns and a second header
                 tier grouping them under the stylist they belong to. -->
            <div v-else class="cal-wrap">
              <div v-if="!gridColumns.length" class="grid-empty">
                {{ staffOptions.length
                  ? "Every stylist is hidden — tick one above to see their schedule."
                  : "No service staff assigned to this sale channel yet." }}
              </div>

              <div v-else ref="gridScroll" class="tg-scroll">
                <div
                  class="tg-inner"
                  :class="{ 'has-groups': view === 'week' }"
                  :style="{
                    '--row-h': rowHeight + 'px',
                    '--rows': gridRowCount,
                    gridTemplateColumns:
                      'var(--tg-gutter) repeat(' + gridColumns.length + ', minmax(' + gridMinWidth + ', 1fr))'
                  }"
                >
                  <template v-if="view === 'week'">
                    <div class="tg-corner tg-corner-top"></div>
                    <div
                      v-for="group in weekGroups"
                      :key="'g' + group.staff_id"
                      class="tg-grouphead"
                    >
                      <span class="tg-colhead-name">{{ group.staff_name }}</span>
                      <span class="tg-colhead-count">{{ group.total }}</span>
                    </div>
                  </template>

                  <div class="tg-corner"></div>
                  <div
                    v-for="col in gridColumns"
                    :key="'h' + col.key"
                    class="tg-colhead"
                    :class="{
                      'is-today': view === 'week' && col.date === todayKey,
                      'is-group-end': col.groupEnd
                    }"
                  >
                    <span class="tg-colhead-name">{{ col.head }}</span>
                    <span v-if="col.sub" class="tg-colhead-sub">{{ col.sub }}</span>
                    <span v-if="col.count !== null" class="tg-colhead-count">{{ col.count }}</span>
                    <!-- SH038 — a whole-day block starts at midnight, so its own label and
                         remove button sit hours above wherever the grid is scrolled to.
                         The header is the one part of the column always in view. -->
                    <span v-if="col.dayOff" class="tg-colhead-off" :title="offTitle(col.dayOff)">
                      Off
                      <button
                        class="tg-off-x tg-off-x-head"
                        type="button"
                        title="Remove this block"
                        @mousedown.stop
                        @click.stop="removeTimeOff(col.dayOff)"
                      >&times;</button>
                    </span>
                    <!-- SH033 — the channel itself is shut that day, which is not the same
                         as the stylist being away and has no block to remove. -->
                    <span
                      v-else-if="col.closed"
                      class="tg-colhead-closed"
                      title="This sale channel is closed all day"
                    >Closed</span>
                    <!-- SH045 — the stylist does not work this day at all. The same chip a
                         day off gets, without the × : there is no block to remove. -->
                    <span
                      v-else-if="col.offAllDay"
                      class="tg-colhead-off"
                      :title="offShiftAllDayTitle"
                    >Off</span>
                  </div>

                  <div class="tg-gutter">
                    <div v-for="h in hourRows" :key="h.hour" class="tg-hour">
                      <span class="tg-hour-label">{{ h.label }}</span>
                    </div>
                  </div>

                  <div
                    v-for="col in gridColumns"
                    :key="'c' + col.key"
                    class="tg-col"
                    :class="{
                      'is-group-end': col.groupEnd,
                      'is-blocking': isBlockable,
                      'is-closed': col.closed
                    }"
                    @click="onSlotClick(col, $event)"
                    @mousedown="startOffDrag(col, $event)"
                    @mousemove="extendOffDrag(col, $event)"
                  >
                    <!-- SH033 — the hours this sale channel is shut. Under everything else:
                         these are the backdrop the day is read against, and an appointment
                         already booked into one still has to be visible on top of it. -->
                    <div
                      v-for="band in col.closedBands"
                      :key="band.key"
                      class="tg-shut"
                      :style="{ top: band.top + 'px', height: band.height + 'px' }"
                      :title="band.title"
                    ></div>

                    <!-- SH037 — the hours this stylist is not on shift. Above the closed
                         backdrop but under everything else: the salon being shut and one
                         stylist being off are different facts, and an appointment already
                         standing in either has to stay visible on top.
                         SH045 — locked, and drawn as the same block a day off is, minus the
                         remove button: this one is not undone here but in Setting > Staff. -->
                    <div
                      v-for="band in col.offShiftBands"
                      :key="band.key"
                      class="tg-off is-locked"
                      :style="{ top: band.top + 'px', height: band.height + 'px' }"
                      :title="band.title"
                    >
                      <span class="tg-off-label">Not working</span>
                    </div>

                    <!-- Only on today's column: a red line on next Tuesday is a lie. -->
                    <div
                      v-if="col.date === todayKey && nowVisible"
                      class="tg-now"
                      :style="{ top: nowOffset + 'px' }"
                    ></div>

                    <!-- SH038 — the stylist is away. Drawn under the appointment blocks so
                         a booking that predates the block is never hidden by it. -->
                    <div
                      v-for="b in col.offBlocks"
                      :key="b.key"
                      class="tg-off"
                      :style="{ top: b.top + 'px', height: b.height + 'px' }"
                      :title="offTitle(b.row)"
                      @click.stop
                    >
                      <span class="tg-off-label">{{ b.row.label }}</span>
                      <button
                        class="tg-off-x"
                        type="button"
                        title="Remove this block"
                        @mousedown.stop
                        @click.stop="removeTimeOff(b.row)"
                      >&times;</button>
                    </div>

                    <div
                      v-if="dragPreview(col)"
                      class="tg-off is-preview"
                      :style="{ top: dragPreview(col).top + 'px', height: dragPreview(col).height + 'px' }"
                    >
                      <span class="tg-off-label">{{ dragPreview(col).label }}</span>
                    </div>

                    <div
                      v-for="b in col.blocks"
                      :key="b.key"
                      class="tg-block"
                      :class="{ 'is-short': b.height < 28, 'is-narrow': view === 'week' }"
                      :style="{
                        top: b.top + 'px',
                        height: b.height + 'px',
                        left: (b.lane * 100 / b.lanes) + '%',
                        width: (100 / b.lanes) + '%'
                      }"
                      :title="chipTitle(b.ev)"
                      @mousedown.stop
                      @click.stop="openEditForm(b.ev)"
                    >
                      <div class="tg-block-time">{{ b.ev.time_text }} – {{ b.ev.end_time_text }}</div>
                      <div class="tg-block-name">{{ b.ev.customer_name }}</div>
                      <div v-if="b.ev.service_name" class="tg-block-service">{{ b.ev.service_name }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Selected day detail. Month view only: day and week already list the day. -->
            <div v-if="view === 'month'" class="day-panel mt-3">
              <div class="day-panel-head">
                {{ selectedDate ? formatDateLong(selectedDate) : "Select a day" }}
                <span class="conn-muted ms-2">{{ selectedEvents.length }} appointment(s)</span>
              </div>

              <div v-if="!selectedEvents.length" class="day-empty">
                {{ hiddenStaffIds.length && dayTotal ? "Every appointment on this day belongs to a hidden stylist." : "No appointments on this day." }}
              </div>

              <div
                v-for="ev in selectedEvents"
                :key="ev.key"
                class="appt-row is-clickable"
                @click="openEditForm(ev)"
              >
                <div class="appt-time">
                  {{ ev.time_text }} – {{ ev.end_time_text }}
                </div>
                <div class="appt-main">
                  <div class="appt-name">
                    {{ ev.customer_name }}
                    <span v-if="ev.service_name" class="appt-service">· {{ ev.service_name }}</span>
                  </div>
                  <div class="appt-meta">{{ apptMeta(ev) }}</div>
                </div>
                <a
                  v-if="ev.html_link"
                  :href="ev.html_link"
                  target="_blank"
                  rel="noopener"
                  class="appt-link"
                  @click.stop
                >Open in Google ↗</a>
              </div>
            </div>

              </div>

              <!-- Two months side by side: the merchant books ahead, and a single month
                   makes "the 2nd of next month" a navigation errand. -->
              <aside class="cal-rail">
                <div v-for="m in miniMonths" :key="m.key" class="mini-cal">
                  <div class="mini-head">
                    <button
                      class="mini-nav"
                      :class="{ 'is-hidden': !m.first }"
                      type="button"
                      @click="shiftMiniMonths(-1)"
                    >&lsaquo;</button>
                    <span class="mini-title">{{ m.label }}</span>
                    <button
                      class="mini-nav"
                      :class="{ 'is-hidden': m.first }"
                      type="button"
                      @click="shiftMiniMonths(1)"
                    >&rsaquo;</button>
                  </div>

                  <div class="mini-grid">
                    <div v-for="(d, i) in miniWeekdays" :key="'d' + i" class="mini-dow">{{ d }}</div>
                    <div
                      v-for="cell in m.cells"
                      :key="cell.key"
                      class="mini-day"
                      :class="{
                        'is-outside': !cell.inMonth,
                        'in-range': cell.inRange,
                        'is-today': cell.isToday,
                        'is-selected': cell.isSelected
                      }"
                      @click="pickDate(cell.key)"
                    >{{ cell.day }}</div>
                  </div>
                </div>

                <button class="btn-plain rail-today" type="button" @click="goToday">Today</button>
              </aside>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- SH038 — the add-ons of the chosen service. Opened straight after the service is
         picked, because until it is answered neither the price nor the duration of the
         appointment is known. -->
    <div v-if="modifierDialog" class="off-overlay off-overlay-top" @click.self="cancelModifiers">
      <div class="off-card">
        <div class="off-card-head">
          <div>
            <div class="off-card-title">{{ modifierDialog.service.name }}</div>
            <div class="off-card-sub">{{ modifierSummary }}</div>
          </div>
          <button class="off-card-x" type="button" @click="cancelModifiers">&times;</button>
        </div>

        <div v-for="group in modifierDialog.groups" :key="group.group_id" class="addon-group">
          <div class="addon-group-head">
            <span class="addon-group-name">{{ group.name }}</span>
            <span class="addon-group-rule">{{ groupRule(group) }}</span>
          </div>
          <label
            v-for="option in group.options"
            :key="option.item_id"
            class="addon-option"
            :class="{ 'is-on': isOptionOn(group, option) }"
          >
            <!-- Keyed on the answer as well as the option: a radio the browser ticked
                 itself keeps that tick when only its :checked binding changes, so the
                 group would show two dots after a second choice. The key makes Vue
                 replace the input rather than patch it. -->
            <input
              :key="option.item_id + '-' + isOptionOn(group, option)"
              :type="group.max_qty === 1 ? 'radio' : 'checkbox'"
              :name="'g' + group.group_id"
              :checked="isOptionOn(group, option)"
              :disabled="isOptionBlocked(group, option)"
              @change="toggleOption(group, option)"
            />
            <span class="addon-option-name">{{ option.name }}</span>
            <span v-if="option.duration_min" class="addon-option-meta">{{ option.duration_min }} min</span>
            <span v-if="option.price > 0" class="addon-option-price">+{{ money(option.price) }}</span>
          </label>
        </div>

        <div v-if="modifierError" class="conn-strip conn-bad mt-2">{{ modifierError }}</div>

        <div class="off-card-foot mt-3">
          <button class="btn-plain" type="button" @click="cancelModifiers">Cancel</button>
          <button class="btn-plain btn-accent" type="button" @click="confirmModifiers">Done</button>
        </div>
      </div>
    </div>

    <!-- SH038 — one click on a quarter hour, two things it could mean. Asking beats
         guessing: the merchant is as likely to be marking somebody away as booking
         somebody in, and the previous behaviour silently chose one of them. -->
    <div v-if="slotChoice" class="off-overlay" @click.self="closeSlotChoice">
      <div class="off-card">
        <div class="off-card-head">
          <div>
            <div class="off-card-title">{{ slotChoice.staff_name }}</div>
            <div class="off-card-sub">{{ slotChoiceText }}</div>
          </div>
          <button class="off-card-x" type="button" @click="closeSlotChoice">&times;</button>
        </div>

        <div class="choice-list">
          <button class="choice" type="button" @click="chooseAppointment">
            <span class="choice-title">Create appointment</span>
            <span class="choice-sub">
              {{ branchId
                ? "Book a customer in with " + slotChoice.staff_name
                : "Choose a branch on the calendar first — the service list comes from its menu" }}
            </span>
          </button>
          <button class="choice" type="button" @click="chooseDayOff">
            <span class="choice-title">Create day off</span>
            <span class="choice-sub">Mark {{ slotChoice.staff_name }} away — all day, or these hours</span>
          </button>
        </div>
      </div>
    </div>

    <!-- The appointment itself. A dialog rather than a panel above the grid: it is opened
         from a click on the grid, and a form that far from the click reads as unrelated.

         SH035 — one form, two presentations. Creating opens it centred, the way it always
         has; editing slides the same card in from the right, so the appointment stays where
         the merchant clicked it and the grid behind is still readable. The alternative was a
         second copy of these twelve fields, which would drift from this one on the first
         change either side. -->
    <div
      v-if="showCreate"
      class="off-overlay"
      :class="{ 'is-drawer': isEditing }"
      @click.self="closeCreateForm"
    >
      <div class="off-card appt-card">
        <div class="off-card-head">
          <div>
            <div class="off-card-title">{{ isEditing ? "Edit appointment" : "New appointment" }}</div>
            <div class="off-card-sub">{{ createFormSub }}</div>
          </div>
          <button class="off-card-x" type="button" @click="closeCreateForm">&times;</button>
        </div>

        <!-- SH035 — the bill is already open on a terminal with these lines on it, and
             nothing here can reach into POS to correct it. Said once, above the fields it
             explains, rather than as a refusal after the merchant has typed. -->
        <div v-if="isEditing && editing.pos_locked" class="conn-strip conn-hint mb-3">
          This appointment is already on a POS bill, so its service and price are fixed here.
          Everything else can still be changed.
        </div>

        <div class="row g-3">
          <div class="col-md-6">
            <label>Customer name *</label>
            <input v-model="createForm.customer_name" type="text" class="form-control" placeholder="Jane Tan" />
          </div>
          <div class="col-md-6">
            <label>Service *</label>
            <!-- SH038 — picked from the sale channel's menu, never typed: the duration that
                 decides how long the stylist is blocked out, and the price, both live on the
                 menu item and cannot be read off a name somebody keyed in. -->
            <Multiselect
              v-model="createForm.service_item_id"
              :options="serviceOptions"
              :searchable="true"
              :canClear="false"
              :disabled="serviceLocked"
              :loading="servicesLoading"
              :placeholder="servicesPlaceholder"
              @change="onServiceChosen"
            />
            <div v-if="createForm.service_name" class="field-hint">{{ createForm.service_name }}</div>
          </div>
          <div class="col-md-6">
            <!-- SH031 — the phone number is the customer's own bill on POS, so a channel
                 that sends its appointments there cannot take one without it. -->
            <label>Phone{{ sendOrdersToPos ? " *" : "" }}</label>
            <input v-model="createForm.customer_phone" type="text" class="form-control" placeholder="+65 9123 4567" />
            <div v-if="sendOrdersToPos" class="field-hint">
              This branch sends its appointments to POS, and the phone number is what opens
              the customer's bill
            </div>
          </div>
          <div class="col-md-6">
            <label>Email</label>
            <input v-model="createForm.customer_email" type="email" class="form-control" placeholder="jane@email.com" />
            <div class="field-hint">Sends a calendar invite the customer can accept</div>
          </div>
          <div class="col-md-6">
            <label>Date *</label>
            <input v-model="createForm.date" type="date" class="form-control" />
          </div>
          <div class="col-md-6">
            <label>Start time *</label>
            <input v-model="createForm.time" type="time" step="900" class="form-control" />
          </div>
          <!-- Both come from the service and its add-ons, so both are read-only: a price
               typed over the menu's would be the one thing on the appointment that no
               other screen agrees with. -->
          <div class="col-md-4">
            <label>Duration (min) *</label>
            <input :value="createForm.duration_minutes" type="number" class="form-control" readonly />
          </div>
          <div class="col-md-4">
            <label>Price</label>
            <input :value="priceText" type="text" class="form-control" readonly />
          </div>
          <div class="col-md-4">
            <label>Stylist *</label>
            <!-- A typed name cannot be checked against anyone's diary; a picked one can. -->
            <select v-model.number="createForm.staff_id" class="form-control">
              <option value="" disabled>Select a stylist</option>
              <option v-for="staff in staffOptions" :key="staff.staff_id" :value="staff.staff_id">
                {{ staff.staff_name }}
              </option>
            </select>
          </div>
          <div class="col-md-12">
            <label>Note</label>
            <input v-model="createForm.note" type="text" class="form-control" placeholder="Anything the stylist should know" />
            <div class="field-hint">Goes on the customer's confirmation and on the POS ticket</div>
          </div>
          <!-- SH035 — the memo. Deliberately not the note above it: the note is written for
               the customer and the stylist to read, this one only the counter ever sees. -->
          <div v-if="isEditing" class="col-md-12">
            <label>Memo</label>
            <textarea
              v-model="createForm.merchant_memo"
              class="form-control"
              rows="3"
              maxlength="1000"
              placeholder="For your team only — e.g. always runs late, prefers the quiet chair"
            ></textarea>
            <div class="field-hint">
              Internal. The customer never sees this — it is on neither their confirmation
              nor the POS ticket.
            </div>
          </div>
        </div>

        <div class="off-card-foot mt-3">
          <a
            v-if="isEditing && editing.html_link"
            :href="editing.html_link"
            target="_blank"
            rel="noopener"
            class="appt-link me-auto"
          >Open in Google ↗</a>
          <button class="btn-plain" type="button" @click="closeCreateForm">Cancel</button>
          <!-- SH040 — only ever on an appointment that exists. Sat next to Save rather than
               up in the header so it reads as one of the two things the drawer can do to
               this appointment, and asks before it does anything. -->
          <button
            v-if="isEditing"
            class="btn-plain btn-danger-plain"
            type="button"
            @click="askDeleteAppointment"
          >
            Delete
          </button>
          <button v-if="isEditing" class="btn-plain btn-accent" type="button" @click="submitEdit">
            Save changes
          </button>
          <button v-else class="btn-plain btn-accent" type="button" @click="submitCreate">
            Create appointment
          </button>
        </div>
      </div>
    </div>

    <!-- SH038 — what the drag turned into, before it is saved. A plain overlay rather than
         a Bootstrap modal: this screen never opens one, and the shared .btn-close-modal
         handler would close things it does not own. -->
    <div v-if="timeOffForm" class="off-overlay" @click.self="closeTimeOffForm">
      <div class="off-card">
        <div class="off-card-head">
          <div>
            <div class="off-card-title">Block time off</div>
            <div class="off-card-sub">{{ timeOffForm.staff_name }} · {{ offFormDateText }}</div>
          </div>
          <button class="off-card-x" type="button" @click="closeTimeOffForm">&times;</button>
        </div>

        <div class="off-presets">
          <button
            v-for="preset in offPresets"
            :key="preset.key"
            class="btn-plain btn-mini"
            :class="{ 'btn-accent': isPresetOn(preset) }"
            type="button"
            @click="applyOffPreset(preset)"
          >{{ preset.label }}</button>
        </div>

        <div v-if="!timeOffForm.is_all_day" class="off-times">
          <label class="off-field">
            <span>From</span>
            <input v-model="timeOffForm.start_time" type="time" step="900" class="off-input" />
          </label>
          <label class="off-field">
            <span>To</span>
            <input v-model="timeOffForm.end_time" type="time" step="900" class="off-input" />
          </label>
        </div>

        <label class="off-field off-field-wide">
          <span>Note (optional)</span>
          <input v-model="timeOffForm.note" type="text" maxlength="255" class="off-input" placeholder="Medical leave" />
        </label>

        <div class="off-card-foot">
          <button class="btn-plain" type="button" @click="closeTimeOffForm">Cancel</button>
          <button class="btn-plain btn-accent" type="button" @click="submitTimeOff">Save block</button>
        </div>
      </div>
    </div>

    <!-- SH039 — removing a block is the one destructive thing on this screen, and it used
         to ask with a browser confirm(): a grey system box, unstyled, that says nothing
         about which block is about to go. This is the same card every other question on
         this screen is asked in, and it names the block. -->
    <div v-if="offToRemove" class="off-overlay" @click.self="closeRemoveOff">
      <div class="off-card">
        <div class="off-card-head">
          <div>
            <div class="off-card-title">Remove day off</div>
            <div class="off-card-sub">{{ offToRemove.staff_name || "Staff" }} &middot; {{ offRemoveDateText }}</div>
          </div>
          <button class="off-card-x" type="button" @click="closeRemoveOff">&times;</button>
        </div>

        <div class="off-remove-block">
          <span class="off-remove-when">{{ offToRemove.is_all_day ? "All day" : offToRemove.time_text + " \u2013 " + offToRemove.end_time_text }}</span>
          <span v-if="offToRemove.note" class="off-remove-note">{{ offToRemove.note }}</span>
        </div>

        <p class="off-remove-text">
          This block will be removed and the time becomes bookable again.
        </p>

        <div class="off-card-foot">
          <button class="btn-plain" type="button" @click="closeRemoveOff">Cancel</button>
          <button class="btn-plain btn-danger-plain" type="button" @click="confirmRemoveOff">Remove</button>
        </div>
      </div>
    </div>

    <!-- SH040 — deleting an appointment is not undoable and the customer is emailed about
         it, so it is asked in the same card SH039 asks about a day off in, and it names the
         customer, the time and the fact that they will be told. -->
    <div v-if="apptToDelete" class="off-overlay" @click.self="closeDeleteAppointment">
      <div class="off-card">
        <div class="off-card-head">
          <div>
            <div class="off-card-title">Delete appointment</div>
            <!-- Who, only. The when is in the block below and saying it twice reads as two
                 different appointments at a glance. -->
            <div class="off-card-sub">{{ apptToDelete.customer_name || "Customer" }}</div>
          </div>
          <button class="off-card-x" type="button" @click="closeDeleteAppointment">&times;</button>
        </div>

        <div class="off-remove-block">
          <span class="off-remove-when">{{ apptDeleteWhenText }}</span>
          <span v-if="apptToDelete.service_name" class="off-remove-note">{{ apptToDelete.service_name }}</span>
        </div>

        <p class="off-remove-text">
          This appointment will be removed and the time becomes bookable again.
          <template v-if="apptToDelete.customer_email">
            {{ apptToDelete.customer_email }} will be emailed that it is cancelled.
          </template>
          <template v-else>
            There is no email address on it, so the customer will not be told.
          </template>
        </p>

        <div class="off-card-foot">
          <button class="btn-plain" type="button" @click="closeDeleteAppointment">Cancel</button>
          <button class="btn-plain btn-danger-plain" type="button" @click="confirmDeleteAppointment">Delete</button>
        </div>
      </div>
    </div>

    <div class="position-fixed top-1 end-1 z-index-2 message-success">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title
        date
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="closeSnackbar"
      />
    </div>
    <material-loading :active="loaderActive" />
  </div>
</template>

<script>
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import MaterialLoading from "@/components/MaterialLoading.vue";
import Multiselect from "@vueform/multiselect";
import moment from "moment";
import BookingService from "../../services/booking.service";
import TestService from "../../services/test.service";

const DATE_KEY = "YYYY-MM-DD";

// SH045 — the hours a stylist is not on shift are locked on this screen and unlocked on
// another one, so every refusal has to say which.
const OFF_SHIFT_HINT =
  "Set their hours in Setting > Staff > Work Schedule to book then.";

export default {
  name: "appointment-calendar",
  components: {
    MaterialSnackbar,
    MaterialLoading,
    Multiselect,
  },
  data() {
    return {
      snackbar: null,
      message: "",
      loaderActive: false,

      branches: [],
      branchId: 0,

      view: "month",
      // One quarter hour. Kept in JS and handed to CSS as --row-h so the block maths and
      // the grid lines can never drift apart.
      rowHeight: 14,
      viewOptions: [
        { value: "day", label: "Day" },
        { value: "week", label: "Week" },
        { value: "month", label: "Month" },
      ],

      cursor: moment().startOf("month"),
      // The rail pages on its own — looking two months ahead should not move the grid
      // out from under the merchant — but follows the grid whenever that leaves the pair.
      miniCursor: moment().startOf("month"),
      selectedDate: moment().format(DATE_KEY),
      weekdays: ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"],
      miniWeekdays: ["S", "M", "T", "W", "T", "F", "S"],

      appointments: [],
      staffOptions: [],
      // SH033 — when each date in the window is open, keyed by date, straight off the sale
      // channel's Operating Hours. An empty object means the API could not say, and the
      // grid falls back to the full 24 hours it drew before this existed.
      operationHours: {},
      // SH037 — the shifts each stylist is on, keyed by staff id and then by date. A
      // stylist absent from this map has no schedule configured at all, which is a
      // different fact from working no hours: their column is left alone.
      staffHours: {},
      // Hidden, not shown: a stylist assigned after the merchant made their choice must
      // appear on the calendar rather than being filtered out by a stale "shown" list.
      hiddenStaffIds: [],
      calendarError: "",
      connection: { connected: false, summary: "", timezone: "", client_email: "", error: "" },

      // SH034 — the id an unassigned appointment carries. Nothing can be booked on it any
      // more, so it is only kept to recognise the older rows the calendar filters out.
      noPreferenceStaffId: 0,

      showCreate: false,
      createForm: this.blankCreateForm(),
      // SH035 — the appointment the same form is editing, or null when it is creating one.
      // Holds the row straight off the calendar, so `event_id` and `pos_locked` travel with
      // it rather than being looked up again at save time.
      editing: null,

      // SH038 — the sale channel's menu, loaded once per channel, and the add-on dialog
      // that opens on top of the appointment form once a service is chosen.
      services: [],
      servicesLoading: false,
      servicesBranchId: null,
      // SH031 — whether the chosen branch opens a POS bill for every appointment. Comes back
      // with its menu, because the menu is fetched the moment a branch is chosen.
      sendOrdersToPos: false,
      modifierDialog: null,
      modifierError: "",

      // SH038 — the absences drawn on this window, and the state of drawing a new one.
      timeOff: [],
      // { colKey, staffId, staffName, date, fromMin, toMin, moved } while the mouse is down.
      drag: null,
      // The stretch that was clicked or dragged, while the merchant says what they meant
      // by it: { staff_id, staff_name, date, from_min, to_min, dragged }.
      slotChoice: null,
      // The pending block, once the drag ends. Null when nothing is being confirmed.
      timeOffForm: null,
      // SH039 — the block whose x was clicked, while the merchant confirms removing it.
      offToRemove: null,
      // SH040 — the appointment whose Delete was clicked, while the merchant confirms it.
      apptToDelete: null,
    };
  },
  computed: {
    /** What one screenful covers — and therefore what gets asked of the API. */
    windowRange() {
      if (this.view === "day") {
        const day = moment(this.selectedDate);
        return { start: day.clone().startOf("day"), end: day.clone().endOf("day") };
      }

      if (this.view === "week") {
        const day = moment(this.selectedDate);
        return { start: day.clone().startOf("week"), end: day.clone().endOf("week") };
      }

      return {
        start: this.cursor.clone().startOf("month").startOf("week"),
        end: this.cursor.clone().endOf("month").endOf("week"),
      };
    },

    rangeLabel() {
      if (this.view === "day") {
        return moment(this.selectedDate).format("dddd, D MMMM YYYY");
      }

      if (this.view === "week") {
        const range = this.windowRange;
        return range.start.isSame(range.end, "month")
          ? range.start.format("D") + " – " + range.end.format("D MMMM YYYY")
          : range.start.format("D MMM") + " – " + range.end.format("D MMM YYYY");
      }

      return this.cursor.format("MMMM YYYY");
    },

    /**
     * The rail. The band marks what the grid on the left is currently showing, so the two
     * little months read as a map of the big one rather than a second, rival calendar.
     */
    miniMonths() {
      const today = moment().format(DATE_KEY);
      const range = this.windowRange;

      return [0, 1].map((offset) => {
        const base = this.miniCursor.clone().add(offset, "month");
        const start = base.clone().startOf("month").startOf("week");
        const cells = [];

        for (let i = 0; i < 42; i++) {
          const day = start.clone().add(i, "days");
          const key = day.format(DATE_KEY);
          cells.push({
            key,
            day: day.date(),
            inMonth: day.month() === base.month(),
            isToday: key === today,
            isSelected: key === this.selectedDate,
            inRange: day.isBetween(range.start, range.end, "day", "[]"),
          });
        }

        return {
          key: base.format("YYYY-MM"),
          label: base.format("MMMM YYYY"),
          first: offset === 0,
          cells,
        };
      });
    },

    /** The dates the grid is currently drawing, in column order. */
    gridDates() {
      if (this.view === "day") return [this.selectedDate];
      if (this.view === "week") return this.weekDays.map((day) => day.key);

      return [];
    },

    /**
     * SH033 — the open windows of each shown date as minutes past midnight.
     *
     * A date the API said nothing about is `null`, not `[]`: "we do not know" has to stay
     * distinguishable from "closed all day", or a merchant whose instance predates this
     * would have their whole calendar greyed out.
     */
    openMinutesByDate() {
      const map = {};

      this.gridDates.forEach((date) => {
        const windows = this.operationHours[date];

        map[date] = Array.isArray(windows)
          ? windows
              .map((window) => [this.clockToMinutes(window.start), this.clockToMinutes(window.end)])
              .filter((window) => window[0] !== null && window[1] !== null && window[1] > window[0])
          : null;
      });

      return map;
    },

    /**
     * SH033 — where the grid starts and stops, in minutes past midnight.
     *
     * Opening hours decide it, but they do not get the last word: an appointment or an
     * absence already sitting outside them — booked before the hours were set, or typed
     * straight into Google Calendar — has to stay on screen. Trimming the grid must never
     * be able to hide something the salon still has to honour.
     *
     * Rounded out to whole hours so the gutter labels and the hour lines keep meeting.
     */
    gridBounds() {
      const dates = this.gridDates;

      if (!dates.length) return { startMin: 0, endMin: 1440 };

      let start = null;
      let end = null;

      const stretch = (from, to) => {
        start = start === null ? from : Math.min(start, from);
        end = end === null ? to : Math.max(end, to);
      };

      const unknown = dates.some((date) => this.openMinutesByDate[date] === null);

      if (unknown) {
        // One unreadable date and the grid is back to the full day. Anything narrower would
        // be a guess about hours nobody told us.
        return { startMin: 0, endMin: 1440 };
      }

      dates.forEach((date) => {
        (this.openMinutesByDate[date] || []).forEach((window) => stretch(window[0], window[1]));
      });

      this.visibleAppointments.forEach((ev) => {
        if (dates.indexOf(ev.date) === -1) return;

        stretch(this.minutesInDay(ev.start_at, ev.date), this.minutesInDay(ev.end_at, ev.date));
      });

      this.timeOff.forEach((row) => {
        if (dates.indexOf(row.date) === -1 || row.is_all_day) return;

        stretch(this.minutesInDay(row.start_at, row.date), this.minutesInDay(row.end_at, row.date));
      });

      // Every shown date closed, and nothing booked on any of them. A working day's worth
      // of greyed-out grid says "shut" more clearly than a blank page does.
      if (start === null) return { startMin: 8 * 60, endMin: 20 * 60 };

      const startMin = Math.max(0, Math.floor(start / 60) * 60);
      const endMin = Math.min(1440, Math.ceil(end / 60) * 60);

      // A channel open 9:00 to 9:30 would otherwise draw a grid one row tall.
      return { startMin, endMin: Math.max(endMin, Math.min(1440, startMin + 120)) };
    },

    /** Minutes past midnight the top edge of the grid stands for. */
    gridStartMin() {
      return this.gridBounds.startMin;
    },

    /** How many quarter hours tall the columns are — handed to CSS as --rows. */
    gridRowCount() {
      return (this.gridBounds.endMin - this.gridBounds.startMin) / 15;
    },

    hourRows() {
      const rows = [];
      const bounds = this.gridBounds;

      for (let minutes = bounds.startMin; minutes < bounds.endMin; minutes += 60) {
        rows.push({
          hour: minutes / 60,
          label: moment().startOf("day").add(minutes, "minutes").format("h A"),
        });
      }

      return rows;
    },

    todayKey() {
      return moment().format(DATE_KEY);
    },

    /** Whether the time "now" is inside the hours the grid is drawing at all. */
    nowVisible() {
      const now = moment();
      const minutes = now.hours() * 60 + now.minutes();

      return minutes >= this.gridBounds.startMin && minutes <= this.gridBounds.endMin;
    },

    /**
     * SH033 — the stretches inside the drawn range that this sale channel is shut, per date.
     *
     * The complement of the open windows, clipped to the grid. A date with no hours at all
     * is one band covering the whole column; a date the API said nothing about has none, so
     * an instance that cannot answer behaves exactly as it did before.
     */
    closedBandsByDate() {
      const bounds = this.gridBounds;
      const map = {};

      this.gridDates.forEach((date) => {
        const windows = this.openMinutesByDate[date];

        if (windows === null) {
          map[date] = [];
          return;
        }

        const bands = [];
        let cursor = bounds.startMin;

        windows
          .slice()
          .sort((a, b) => a[0] - b[0])
          .forEach((window) => {
            const from = Math.max(window[0], bounds.startMin);
            const to = Math.min(window[1], bounds.endMin);

            if (from > cursor) bands.push([cursor, from]);
            cursor = Math.max(cursor, to);
          });

        if (cursor < bounds.endMin) bands.push([cursor, bounds.endMin]);

        map[date] = bands.map((band) => ({
          key: date + "|" + band[0],
          fromMin: band[0],
          toMin: band[1],
          top: this.topFor(band[0]),
          height: ((band[1] - band[0]) / 15) * this.rowHeight,
          title: "Closed — this sale channel is not open " +
            this.minutesToLabel(band[0]) + " to " + this.minutesToLabel(band[1]),
        }));
      });

      return map;
    },

    /** Where "now" sits. Which column draws it is decided per column, not here. */
    nowOffset() {
      const now = moment();

      return this.topFor(now.hours() * 60 + now.minutes());
    },

    /** Seven day columns are a quarter the width of one, and the head reads differently. */
    gridMinWidth() {
      return this.view === "week" ? "64px" : "150px";
    },

    /** The stylists still ticked, in the order the filter panel lists them. */
    visibleStaff() {
      return this.staffOptions.filter((staff) => !this.isStaffHidden(staff.staff_id));
    },

    /** Appointments keyed by "staffId|date" — every column is then a lookup, not a scan. */
    appointmentsByStaffDay() {
      const map = {};

      this.visibleAppointments.forEach((ev) => {
        const key = ev.staff_id + "|" + ev.date;

        if (!map[key]) map[key] = [];
        map[key].push(ev);
      });

      return map;
    },

    /** The seven days a week view covers. */
    weekDays() {
      const range = this.windowRange;
      const days = [];
      const day = range.start.clone();

      while (day.isSameOrBefore(range.end, "day")) {
        days.push({ key: day.format(DATE_KEY), weekday: day.format("ddd"), num: day.format("D") });
        day.add(1, "day");
      }

      return days;
    },

    /** The first header tier: one cell per stylist, spanning that stylist's seven days. */
    weekGroups() {
      return this.visibleStaff.map((staff) => {
        const days = this.weekDays.map(
          (day) => (this.appointmentsByStaffDay[staff.staff_id + "|" + day.key] || []).length
        );

        return {
          staff_id: staff.staff_id,
          staff_name: staff.staff_name,
          total: days.reduce((sum, n) => sum + n, 0),
        };
      });
    },

    /**
     * The columns themselves, flat, because a CSS grid places children in document order —
     * nesting a v-for per stylist would break the alignment the whole layout rests on.
     *
     * A stylist with an empty day keeps their column: the gap is the point of the screen.
     */
    gridColumns() {
      if (this.view === "day") {
        return this.visibleStaff.map((staff) => {
          const events = this.appointmentsByStaffDay[staff.staff_id + "|" + this.selectedDate] || [];
          const offShift = this.offShiftBandsFor(staff.staff_id, this.selectedDate);

          return {
            key: "s" + staff.staff_id,
            head: staff.staff_name,
            sub: null,
            count: events.length,
            date: this.selectedDate,
            blocks: this.layoutBlocks(events, this.selectedDate),
            offBlocks: this.layoutOffBlocks(staff.staff_id, this.selectedDate),
            dayOff: this.allDayOffFor(staff.staff_id, this.selectedDate),
            closedBands: this.closedBandsFor(this.selectedDate),
            offShiftBands: offShift,
            offAllDay: offShift.some((band) => band.allDay),
            closed: this.isClosedAllDay(this.selectedDate),
          };
        });
      }

      if (this.view !== "week") return [];

      const columns = [];

      this.visibleStaff.forEach((staff) => {
        this.weekDays.forEach((day, index) => {
          const offShift = this.offShiftBandsFor(staff.staff_id, day.key);

          columns.push({
            key: staff.staff_id + "|" + day.key,
            head: day.weekday,
            sub: day.num,
            count: null,
            date: day.key,
            groupEnd: index === this.weekDays.length - 1,
            blocks: this.layoutBlocks(
              this.appointmentsByStaffDay[staff.staff_id + "|" + day.key] || [],
              day.key
            ),
            offBlocks: this.layoutOffBlocks(staff.staff_id, day.key),
            dayOff: this.allDayOffFor(staff.staff_id, day.key),
            closedBands: this.closedBandsFor(day.key),
            offShiftBands: offShift,
            offAllDay: offShift.some((band) => band.allDay),
            closed: this.isClosedAllDay(day.key),
          });
        });
      });

      return columns;
    },

    /**
     * SH038 — the blocks bucketed the way the columns read them, and the three presets the
     * confirm card offers.
     *
     * Morning runs to 1:00 PM rather than to noon, matching where the booking flow already
     * splits its Morning and Afternoon headings. The bounds are midnight and midnight
     * rather than the outlet's opening hours on purpose: blocking an hour the salon is shut
     * takes nothing away, and the alternative is a screen that has to know every channel's
     * opening times before it can offer a half day.
     */
    timeOffByStaffDay() {
      const map = {};
      this.timeOff.forEach((row) => {
        const key = row.staff_id + "|" + row.date;
        if (!map[key]) map[key] = [];
        map[key].push(row);
      });
      return map;
    },

    timeOffByDate() {
      const map = {};
      this.timeOff.forEach((row) => {
        if (!map[row.date]) map[row.date] = [];
        map[row.date].push(row);
      });
      return map;
    },

    offPresets() {
      return [
        { key: "all", label: "All day", all_day: true },
        { key: "morning", label: "Morning (to 1:00 PM)", start: "00:00", end: "13:00" },
        { key: "afternoon", label: "Afternoon (from 1:00 PM)", start: "13:00", end: "00:00" },
      ];
    },

    offFormDateText() {
      return this.timeOffForm ? moment(this.timeOffForm.date).format("ddd, D MMM YYYY") : "";
    },

    /** SH040 — when the appointment being deleted was, spelled out for the confirmation. */
    apptDeleteWhenText() {
      const ev = this.apptToDelete;

      if (!ev) return "";

      const start = moment(ev.start_at, "YYYY-MM-DD HH:mm:ss");

      return start.isValid()
        ? start.format("ddd, D MMM YYYY") + " \u00b7 " + start.format("h:mm A")
        : ev.date || "";
    },

    /** SH039 — the date of the block being removed, spelled out for the confirmation. */
    offRemoveDateText() {
      return this.offToRemove ? moment(this.offToRemove.date).format("ddd, D MMM YYYY") : "";
    },

    slotChoiceText() {
      const choice = this.slotChoice;

      if (!choice) return "";

      const when = choice.dragged
        // A dragged stretch is the answer to both questions, so it is printed whole.
        ? this.minutesToLabel(choice.from_min) + " - " + this.minutesToLabel(choice.to_min)
        : this.minutesToLabel(choice.from_min);

      return moment(choice.date).format("ddd, D MMM YYYY") + " · " + when;
    },

    /** The picker's options: name first, with what it costs and how long it takes. */
    serviceOptions() {
      return this.services.map((service) => ({
        value: service.item_id,
        label: service.name +
          (service.duration_min ? " · " + service.duration_min + " min" : "") +
          " · " + this.money(service.price),
      }));
    },

    servicesPlaceholder() {
      if (this.servicesLoading) return "Loading services…";

      return this.services.length ? "Choose a service" : "No services on this menu";
    },

    priceText() {
      return this.createForm.price === null ? "" : this.money(this.createForm.price);
    },

    isEditing() {
      return this.editing !== null;
    },

    /** SH035 — POS has already billed these lines, so the menu picker is read-only. */
    serviceLocked() {
      return this.isEditing && !!this.editing.pos_locked;
    },

    /**
     * Which channel's menu the form is working against.
     *
     * Creating, that is whatever channel the calendar is filtered to — requireBranch() has
     * already insisted on one. Editing, it is the appointment's own channel, so an
     * appointment opened from the All Branches view still shows the services it was booked
     * from rather than an empty picker.
     */
    formBranchId() {
      if (this.isEditing) {
        return Number(this.editing.branch_id || this.branchId || 0);
      }

      return Number(this.branchId || 0);
    },

    /** "45 min · $45.00" as the add-on dialog stands right now. */
    modifierSummary() {
      if (!this.modifierDialog) return "";

      const totals = this.modifierTotals();

      return totals.duration + " min · " + this.money(totals.price);
    },

    createFormSub() {
      const f = this.createForm;

      if (!f.date) return "";

      const staffName = this.staffLabel(f.staff_id);

      return moment(f.date).format("ddd, D MMM YYYY") +
        (f.time ? " · " + moment(f.date + " " + f.time, "YYYY-MM-DD HH:mm").format("h:mm A") : "") +
        (staffName ? " · " + staffName : "");
    },

    /**
     * Are the two-option popup and the block-out drag on offer?
     *
     * The day view only — the week's columns are a seventh of the width, and a drag across
     * one is more likely a mis-click than a shift being blocked out. SH034 left every
     * column standing for a real stylist, so nothing else is excluded.
     */
    isBlockable() {
      return this.view === "day";
    },

    /** SH045 — why the column is locked, and where the merchant unlocks it. */
    offShiftAllDayTitle() {
      return "Not working — this stylist does not work on this day. " + OFF_SHIFT_HINT;
    },

    /** Hiding is instant: the whole window is already loaded, so no round trip. */
    visibleAppointments() {
      if (!this.hiddenStaffIds.length) return this.appointments;
      return this.appointments.filter(
        (ev) => !this.hiddenStaffIds.includes(ev.staff_id)
      );
    },

    visibleStaffCount() {
      return this.staffOptions.filter((s) => !this.isStaffHidden(s.staff_id)).length;
    },

    /**
     * Bucketed by day so each cell is a lookup rather than a filter. The API hands back
     * the salon's own date already resolved, so no timezone maths happens on the viewer's
     * machine — a merchant looking at the schedule from abroad reads the salon's clock.
     */
    eventsByDate() {
      const map = {};
      this.visibleAppointments.forEach((ev) => {
        if (!ev.date) return;
        if (!map[ev.date]) map[ev.date] = [];
        map[ev.date].push(ev);
      });
      return map;
    },

    /** Appointments on the selected day before the stylist filter — tells an empty day
     *  apart from a day whose appointments are all hidden. */
    dayTotal() {
      return this.appointments.filter((ev) => ev.date === this.selectedDate).length;
    },

    calendarCells() {
      const start = this.cursor.clone().startOf("month").startOf("week");
      const today = moment().format(DATE_KEY);
      const month = this.cursor.month();
      const cells = [];

      for (let i = 0; i < 42; i++) {
        const day = start.clone().add(i, "days");
        const key = day.format(DATE_KEY);
        cells.push({
          key,
          day: day.date(),
          inMonth: day.month() === month,
          isToday: key === today,
          events: this.eventsByDate[key] || [],
          timeOff: this.timeOffByDate[key] || [],
        });
      }
      return cells;
    },

    selectedEvents() {
      return this.eventsByDate[this.selectedDate] || [];
    },
  },
  mounted() {
    // SH039 — the confirmation is the one dialog that stands between a click and losing a
    // block, so Escape backs out of it.
    document.addEventListener("keydown", this.onRemoveOffKeydown);
  },
  beforeUnmount() {
    // SH038 — a drag interrupted by leaving the screen would otherwise leave its mouseup
    // handler on the document for the rest of the session.
    document.removeEventListener("mouseup", this.endOffDrag);
    document.removeEventListener("keydown", this.onRemoveOffKeydown);
  },
  async created() {
    await this.getBranches();
    await this.checkConnection();
    await this.loadAppointments();
  },
  methods: {
    blankCreateForm() {
      return {
        customer_name: "",
        customer_phone: "",
        customer_email: "",
        // SH038 — the menu item behind the appointment, and the name it is written up as
        // once its add-ons are known. duration_minutes and price follow from both.
        service_item_id: null,
        service_name: "",
        service_options: [],
        // SH034 — "" is "nobody picked yet". The appointment cannot be saved on it.
        staff_id: "",
        date: moment().format(DATE_KEY),
        time: "11:00",
        duration_minutes: 45,
        price: null,
        note: "",
        // SH035 — merchant-only, and only ever filled in from the edit drawer.
        merchant_memo: "",
      };
    },

    async getBranches() {
      await TestService.getBranches().then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.branches = response.data.data;
            this.branches.unshift({ value: 0, label: "-- All Branches --" });
          }
        },
        () => {
          // Branch list only filters the view; the calendar still works without it.
        }
      );
    },

    async checkConnection() {
      await BookingService.calendarPing().then(
        (response) => {
          if (response.data.result.isSuccess) {
            const d = response.data.data;
            this.connection = {
              connected: true,
              summary: d.summary,
              timezone: d.timezone,
              client_email: d.client_email,
              error: "",
            };
          } else {
            this.connection = { connected: false, error: response.data.result.message };
          }
        },
        (error) => {
          this.connection = { connected: false, error: String(error) };
        }
      );
    },

    /**
     * SH011 — the whole window in one call: booking rows first, with the calendar-only
     * events merged in. The stylist filter is applied here in the browser rather than by
     * re-fetching, so ticking a name is instant; the endpoint's own staff_ids filter is
     * left for callers that want the narrowed set server-side.
     */
    async loadAppointments() {
      this.showLoader();
      const range = this.windowRange;
      const payload = {
        from_date: range.start.format(DATE_KEY),
        to_date: range.end.format(DATE_KEY),
        branch_id: this.branchId,
      };

      await BookingService.salonAppointments(payload).then(
        (response) => {
          if (response.data.result.isSuccess) {
            const data = response.data.data;
            // SH034 — No Preference is no longer something anybody can pick, on this screen
            // or in MOO, so the calendar drops the bucket entirely: the unassigned column,
            // its filter chip, and the appointments still parked in it.
            this.appointments = (data.appointments || []).filter(
              (ev) => Number(ev.staff_id) !== this.noPreferenceStaffId
            );
            this.staffOptions = (data.staff_options || []).filter(
              (staff) => staff.staff_id !== this.noPreferenceStaffId
            );
            this.timeOff = data.time_off || [];
            this.operationHours = data.operation_hours || {};
            this.staffHours = data.staff_hours || {};
            this.calendarError = data.calendar_error || "";
            this.dropStaleHiddenStaff();
            this.scrollToFirstAppointment();
          } else {
            this.appointments = [];
            this.staffOptions = [];
            this.timeOff = [];
            this.operationHours = {};
            this.staffHours = {};
            this.calendarError = "";
            this.message = response.data.result.message;
            this.snackbar = "danger";
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.appointments = [];
          this.staffOptions = [];
          this.timeOff = [];
          this.operationHours = {};
          this.staffHours = {};
          this.calendarError = "";
          this.message = String(error);
          this.snackbar = "danger";
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },

    /**
     * SH033 — the two conversions between a time of day and a position in the column.
     *
     * The grid no longer starts at midnight, so every top, height and click has to go
     * through these: a stray `minutes / 15 * rowHeight` left behind would place its block
     * a whole opening time out.
     */
    topFor(minutes) {
      return ((minutes - this.gridStartMin) / 15) * this.rowHeight;
    },

    /** The quarter hour a pointer offset inside a column falls in. */
    minutesFromTop(offset) {
      const bounds = this.gridBounds;
      const minutes = bounds.startMin + Math.floor(offset / this.rowHeight) * 15;

      return Math.max(bounds.startMin, Math.min(bounds.endMin - 15, minutes));
    },

    /** "09:30" / "24:00" as minutes past midnight, or null when it is neither. */
    clockToMinutes(clock) {
      const match = /^(\d{1,2}):(\d{2})/.exec(String(clock || "").trim());

      if (!match) return null;

      const minutes = Number(match[1]) * 60 + Number(match[2]);

      return minutes >= 0 && minutes <= 1440 ? minutes : null;
    },

    /** The bands to grey out on one date. Read off the cache, not recomputed per column. */
    closedBandsFor(date) {
      return this.closedBandsByDate[date] || [];
    },

    /**
     * SH037 — the hours one stylist is NOT on shift, as bands to grey out in their column.
     *
     * Per stylist rather than per date, which is what makes this different from
     * closedBandsFor(): the salon is open and somebody else is working those hours.
     *
     * A stylist the API said nothing about has no schedule configured, so nothing is
     * greyed — the same "we do not know" reading openMinutesByDate gives a missing date.
     * A date present with no window is the opposite: a day they do not work at all, and
     * the whole column greys out.
     */
    offShiftBandsFor(staffId, date) {
      const byDate = this.staffHours[staffId] || this.staffHours[String(staffId)];

      if (!byDate) return [];

      const windows = (byDate[date] || [])
        .map((window) => [this.clockToMinutes(window.start), this.clockToMinutes(window.end)])
        .filter((window) => window[0] !== null && window[1] !== null && window[1] > window[0])
        .sort((a, b) => a[0] - b[0]);

      const bounds = this.gridBounds;
      const bands = [];
      let cursor = bounds.startMin;

      windows.forEach((window) => {
        const from = Math.max(window[0], bounds.startMin);
        const to = Math.min(window[1], bounds.endMin);

        if (from > cursor) bands.push([cursor, from]);
        cursor = Math.max(cursor, to);
      });

      if (cursor < bounds.endMin) bands.push([cursor, bounds.endMin]);

      return bands.map((band) => ({
        key: staffId + "|" + date + "|" + band[0],
        // SH045 — the cells these cover are locked, so the bands carry the minutes the
        // click and drag handlers test against rather than pixels alone.
        fromMin: band[0],
        toMin: band[1],
        // A date present in the schedule with no window in it is a day they do not work at
        // all, which the column header says once instead of the band saying it all day.
        allDay: !windows.length,
        top: this.topFor(band[0]),
        height: ((band[1] - band[0]) / 15) * this.rowHeight,
        title:
          (windows.length
            ? "Not working — this stylist is off " +
              this.minutesToLabel(band[0]) + " to " + this.minutesToLabel(band[1])
            : "Not working — this stylist does not work on this day") +
          ". " + OFF_SHIFT_HINT,
      }));
    },

    /** Whether the channel is shut for the whole of one date. */
    isClosedAllDay(date) {
      const windows = this.openMinutesByDate[date];

      return Array.isArray(windows) && windows.length === 0;
    },

    /** Whether [fromMin, toMin) runs into any hour the channel is shut on that date. */
    hitsClosedHours(date, fromMin, toMin) {
      return this.closedBandsFor(date).some(
        (band) => band.fromMin < toMin && band.toMin > fromMin
      );
    },

    /**
     * SH045 — whether [fromMin, toMin) runs into an hour this column's stylist is not on
     * shift. Per column rather than per date: two stylists working the same open salon can
     * disagree about the same quarter hour.
     */
    hitsOffShiftHours(col, fromMin, toMin) {
      return (col.offShiftBands || []).some(
        (band) => band.fromMin < toMin && band.toMin > fromMin
      );
    },

    /**
     * SH045 — refused the way SH033 refuses a closed hour, pointed at the other screen. A
     * day off is undone by removing the block that made it; these cells have no block to
     * remove, so the sentence has to carry the way out or the merchant looks for an × that
     * is deliberately not there.
     */
    sayOffShift(col) {
      const allDay = (col.offShiftBands || []).some((band) => band.allDay);

      this.message =
        (allDay
          ? col.head + " does not work on this day."
          : col.head + " is not on shift at that time.") +
        " " + OFF_SHIFT_HINT;
      this.snackbar = "danger";
      this.setTimeOutNotification();
    },

    /**
     * Refusing a click on a closed hour out loud rather than doing nothing: silence reads
     * as a broken screen, and the merchant's next move is to change the channel's hours.
     */
    sayClosed(date) {
      this.message = this.isClosedAllDay(date)
        ? "This sale channel is closed all day. Set its Operating Hours in Store Detail to book then."
        : "That time is outside this sale channel's opening hours.";
      this.snackbar = "danger";
      this.setTimeOutNotification();
    },

    /** Minutes past midnight on the given day, clamped to it. */
    minutesInDay(stamp, dayKey) {
      const at = moment(stamp, "YYYY-MM-DD HH:mm:ss");
      const start = moment(dayKey).startOf("day");
      const minutes = at.diff(start, "minutes");

      return Math.max(0, Math.min(1440, minutes));
    },

    /**
     * Places one stylist's appointments in their column.
     *
     * Phase 4 stops a stylist being double-booked in the first place, but rows already in
     * the database can overlap, and a block quietly painted on top of another would hide an
     * appointment the salon still has to honour. Overlapping ones share the column width.
     */
    layoutBlocks(events, dayKey) {
      const sorted = events
        .slice()
        .sort((a, b) => (a.start_at < b.start_at ? -1 : a.start_at > b.start_at ? 1 : 0));

      const blocks = [];
      let cluster = [];
      let laneEnds = [];
      let clusterEnd = -1;

      // A cluster is a run of appointments that touch. Width is shared inside one, so it
      // cannot be settled until the run ends.
      const closeCluster = () => {
        cluster.forEach((block) => {
          block.lanes = laneEnds.length;
        });
        cluster = [];
        laneEnds = [];
        clusterEnd = -1;
      };

      sorted.forEach((ev) => {
        const start = this.minutesInDay(ev.start_at, dayKey);
        // A zero-length appointment would be an invisible block; give it one row.
        const end = Math.max(this.minutesInDay(ev.end_at, dayKey), start + 15);

        if (cluster.length && start >= clusterEnd) {
          closeCluster();
        }

        let lane = laneEnds.findIndex((laneEnd) => laneEnd <= start);

        if (lane === -1) {
          laneEnds.push(end);
          lane = laneEnds.length - 1;
        } else {
          laneEnds[lane] = end;
        }

        const block = {
          key: ev.key,
          ev,
          startMin: start,
          endMin: end,
          top: this.topFor(start),
          height: ((end - start) / 15) * this.rowHeight,
          lane,
          lanes: 1,
        };

        cluster.push(block);
        blocks.push(block);
        clusterEnd = Math.max(clusterEnd, end);
      });

      closeCluster();

      return blocks;
    },

    /**
     * SH033 — the grid now starts at opening time, so there is usually nothing to scroll
     * past. It still opens on the earliest appointment when one sits above the fold, which
     * is what an outlet open round the clock, or one with a 6:30 blow-dry booked before it
     * opened, needs.
     */
    scrollToFirstAppointment() {
      this.$nextTick(() => {
        const scroller = this.$refs.gridScroll;

        if (!scroller) return;

        const starts = this.gridColumns
          .reduce((all, col) => all.concat(col.blocks), [])
          .map((block) => block.top);

        const target = starts.length ? Math.min.apply(null, starts) : 0;

        scroller.scrollTop = Math.max(0, target - this.rowHeight * 2);
      });
    },

    /**
     * Clicking an empty quarter hour starts an appointment there. Clicking one that is
     * already taken does nothing: the stylist is busy, and opening a form pre-filled with a
     * time that will be rejected on submit is a worse answer than not opening at all.
     */
    onSlotClick(col, event) {
      // SH038 — where the two-option popup applies, the mouseup that ended the press has
      // already opened it. Opening a second thing on the same gesture would ask the
      // merchant twice. What is left here is the week grid, where a click can only ever
      // mean one thing: book somebody in.
      if (this.isBlockable) return;

      const minutes = this.minutesAt(event);

      // SH033 — an hour the channel is shut is not bookable, so the form does not open on
      // one. The message says why; doing nothing at all would read as a dead grid.
      if (this.hitsClosedHours(col.date, minutes, minutes + 15)) {
        this.sayClosed(col.date);
        return;
      }

      // SH045 — the salon is open but this stylist is not on shift. Locked for the same
      // reason and answered the same way: out loud, naming where the hours are set.
      if (this.hitsOffShiftHours(col, minutes, minutes + 15)) {
        this.sayOffShift(col);
        return;
      }

      const staffId = this.columnStaffId(col);
      const busy = col.blocks.some(
        (block) => minutes >= block.startMin && minutes < block.endMin
      );

      if (busy) return;

      const pad = (n) => String(n).padStart(2, "0");

      this.openCreateForm({
        staff_id: staffId,
        date: col.date,
        time: pad(Math.floor(minutes / 60)) + ":" + pad(minutes % 60),
      });
    },

    // ------------------------------------------------- SH038 · the service and its add-ons

    money(amount) {
      return "$" + Number(amount || 0).toFixed(2);
    },

    /**
     * The channel's menu, fetched the first time the form needs it and again whenever the
     * merchant switches channel — a menu belongs to one channel, and the same service can
     * be priced differently in the next.
     */
    async loadServices() {
      const branchId = this.formBranchId;

      if (!branchId) {
        // Nothing to load: the menu belongs to a branch.
        this.services = [];
        this.servicesBranchId = null;
        this.sendOrdersToPos = false;
        return;
      }

      if (this.servicesBranchId === branchId && this.services.length) return;

      this.servicesLoading = true;

      await BookingService.salonServices({ branch_id: branchId }).then(
        (response) => {
          if (response.data.result.isSuccess) {
            this.services = response.data.data.services || [];
            this.servicesBranchId = branchId;
            this.sendOrdersToPos =
              Number(response.data.data.send_orders_to_pos || 0) === 1;
          } else {
            this.services = [];
            this.sendOrdersToPos = false;
            this.message = response.data.result.message;
            this.snackbar = "danger";
            this.setTimeOutNotification();
          }
          this.servicesLoading = false;
        },
        (error) => {
          this.services = [];
          this.sendOrdersToPos = false;
          this.servicesLoading = false;
          this.message = String(error);
          this.snackbar = "danger";
          this.setTimeOutNotification();
        }
      );
    },

    /**
     * A service was picked. Its own price and duration land on the form straight away, and
     * the add-on dialog opens on top when the service has any — an appointment's length is
     * not known until they are answered.
     */
    async onServiceChosen(itemId) {
      const service = this.services.find((row) => row.item_id === itemId);

      if (!service) return;

      this.applyService(service, []);

      if (!service.has_modifiers) return;

      this.showLoader();

      await BookingService.salonServiceDetail({
        branch_id: this.formBranchId,
        item_id: itemId,
      }).then(
        (response) => {
          if (response.data.result.isSuccess) {
            const detail = response.data.data;

            if ((detail.groups || []).length) {
              this.openModifierDialog(detail);
            }
          } else {
            // The service is still usable at its own price; only the add-ons are missing.
            this.message = response.data.result.message;
            this.snackbar = "danger";
            this.setTimeOutNotification();
          }
          this.hideLoader();
        },
        (error) => {
          this.message = String(error);
          this.snackbar = "danger";
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },

    /**
     * Writes a service and its chosen add-ons onto the form.
     *
     * @param {object} service  the menu item
     * @param {Array} options  the add-ons ticked on it
     */
    applyService(service, options) {
      const extraPrice = options.reduce((sum, option) => sum + Number(option.price || 0), 0);
      const extraMinutes = options.reduce((sum, option) => sum + Number(option.duration_min || 0), 0);

      this.createForm.service_item_id = service.item_id;
      this.createForm.service_options = options;
      this.createForm.service_name = options.length
        ? service.name + " + " + options.map((option) => option.name).join(", ")
        : service.name;
      this.createForm.price = Number(service.price || 0) + extraPrice;
      // A service with no duration set on it would otherwise block no time at all; the
      // form's usual default stands in until the merchant fills the menu in.
      this.createForm.duration_minutes = Number(service.duration_min || 0) + extraMinutes || 45;
    },

    openModifierDialog(detail) {
      this.modifierError = "";
      this.modifierDialog = {
        service: detail,
        groups: detail.groups,
        // Chosen option ids per group. A group that must have exactly one starts on its
        // first option, the way the customer's own sheet opens.
        chosen: detail.groups.reduce((map, group) => {
          map[group.group_id] =
            group.min_qty === 1 && group.max_qty === 1 && group.options.length
              ? [group.options[0].item_id]
              : [];
          return map;
        }, {}),
      };
    },

    isOptionOn(group, option) {
      const chosen = this.modifierDialog.chosen[group.group_id] || [];

      return chosen.includes(option.item_id);
    },

    /** A full group blocks what is not already ticked, rather than silently swapping one. */
    isOptionBlocked(group, option) {
      if (group.max_qty <= 1) return false;

      const chosen = this.modifierDialog.chosen[group.group_id] || [];

      return chosen.length >= group.max_qty && !chosen.includes(option.item_id);
    },

    toggleOption(group, option) {
      const chosen = this.modifierDialog.chosen[group.group_id] || [];

      if (group.max_qty === 1) {
        this.modifierDialog.chosen[group.group_id] = [option.item_id];
      } else if (chosen.includes(option.item_id)) {
        this.modifierDialog.chosen[group.group_id] = chosen.filter((id) => id !== option.item_id);
      } else if (chosen.length < group.max_qty || group.max_qty === 0) {
        this.modifierDialog.chosen[group.group_id] = chosen.concat([option.item_id]);
      }

      this.modifierError = "";
    },

    groupRule(group) {
      if (group.min_qty > 0 && group.max_qty === 1) return "Required";
      if (group.min_qty > 0) return "Choose at least " + group.min_qty;
      if (group.max_qty > 0) return "Up to " + group.max_qty;

      return "Optional";
    },

    /** Every option ticked across the groups, in the order the groups are drawn. */
    chosenOptions() {
      if (!this.modifierDialog) return [];

      return this.modifierDialog.groups.flatMap((group) =>
        group.options.filter((option) => this.isOptionOn(group, option))
      );
    },

    modifierTotals() {
      const options = this.chosenOptions();

      return {
        price: Number(this.modifierDialog.service.price || 0) +
          options.reduce((sum, option) => sum + Number(option.price || 0), 0),
        duration: Number(this.modifierDialog.service.duration_min || 0) +
          options.reduce((sum, option) => sum + Number(option.duration_min || 0), 0),
      };
    },

    confirmModifiers() {
      const short = this.modifierDialog.groups.find(
        (group) => (this.modifierDialog.chosen[group.group_id] || []).length < group.min_qty
      );

      if (short) {
        this.modifierError = short.name + " needs at least " + short.min_qty + " choice" +
          (short.min_qty > 1 ? "s" : "") + ".";
        return;
      }

      this.applyService(this.modifierDialog.service, this.chosenOptions());
      this.modifierDialog = null;
    },

    cancelModifiers() {
      // The service itself stays chosen, at its own price and length: cancelling the
      // add-ons is not cancelling the service.
      this.modifierDialog = null;
      this.modifierError = "";
    },

    // ------------------------------------------------------------------ SH038

    /** Which quarter hour of the column the pointer is over, as minutes past midnight. */
    minutesAt(event) {
      const bounds = event.currentTarget.getBoundingClientRect();

      return this.minutesFromTop(event.clientY - bounds.top);
    },

    startOffDrag(col, event) {
      if (!this.isBlockable) return;

      // Left button only, and never on top of an existing block's remove button.
      if (event.button !== 0) return;

      const minutes = this.minutesAt(event);

      // SH033 — nothing is drawn on closed hours, neither an appointment nor an absence:
      // a stylist cannot be marked away from a salon that is not open.
      if (this.hitsClosedHours(col.date, minutes, minutes + 15)) {
        this.sayClosed(col.date);
        return;
      }

      // SH045 — nor outside the stylist's own hours: neither an appointment nor an absence
      // belongs on a shift they are not working.
      if (this.hitsOffShiftHours(col, minutes, minutes + 15)) {
        this.sayOffShift(col);
        return;
      }

      const occupant = this.occupantOf(col, minutes, minutes + 15);

      // Nothing can be booked or blocked on top of what is already there, so the popup
      // does not open to ask which of the two the merchant meant.
      if (occupant) {
        this.sayOccupied(occupant);
        return;
      }

      this.drag = {
        colKey: col.key,
        staffId: this.columnStaffId(col),
        staffName: col.head,
        date: col.date,
        fromMin: minutes,
        toMin: minutes + 15,
        // A click is a drag that went nowhere, and the two mean different things: one asks
        // which of the two forms to open, the other has already drawn a block.
        moved: false,
      };

      // On the document, not the column: a drag that runs off the bottom of the grid still
      // has to end somewhere, and a half-finished drag left in state would paint a preview
      // block that never goes away.
      document.addEventListener("mouseup", this.endOffDrag);
    },

    extendOffDrag(col, event) {
      if (!this.drag || this.drag.colKey !== col.key) return;

      const minutes = this.minutesAt(event);

      const toMin = minutes >= this.drag.fromMin ? minutes + 15 : minutes;

      // Dragging upwards is the same block drawn the other way round.
      this.drag = Object.assign({}, this.drag, {
        toMin,
        // A pointer that wobbles inside the quarter hour it started in is still a click.
        moved: this.drag.moved || Math.abs(toMin - this.drag.fromMin) > 15,
      });
    },

    endOffDrag() {
      document.removeEventListener("mouseup", this.endOffDrag);

      if (!this.drag) return;

      const drag = this.drag;
      this.drag = null;

      const from = Math.min(drag.fromMin, drag.toMin);
      const to = Math.max(drag.fromMin, drag.toMin);

      const column = this.gridColumns.find((col) => col.key === drag.colKey);
      const occupant = column ? this.occupantOf(column, from, to) : null;

      // The drag started on free time but ran into something. Refused whole rather than
      // trimmed back: a stretch silently shorter than the one drawn is a worse answer than
      // being told what is in the way.
      if (occupant) {
        this.sayOccupied(occupant);
        return;
      }

      // SH033 — the same refusal for the same reason: dragged past closing time, and a
      // stretch silently trimmed back would be a worse answer than being told.
      if (this.hitsClosedHours(drag.date, from, to)) {
        this.sayClosed(drag.date);
        return;
      }

      // SH045 — and the same for a drag that started inside the shift and ran out of it.
      if (column && this.hitsOffShiftHours(column, from, to)) {
        this.sayOffShift(column);
        return;
      }

      // Both gestures ask the same question. A drag is not a shorthand for "block this
      // out": the merchant who drags out 2:00 to 3:30 is as likely to be sizing an
      // appointment as an absence, and only they know which.
      this.slotChoice = {
        staff_id: drag.staffId,
        staff_name: drag.staffName,
        date: drag.date,
        from_min: from,
        to_min: to,
        dragged: drag.moved,
      };
    },

    /** The band under the cursor while the mouse is down, or null for other columns. */
    dragPreview(col) {
      if (!this.drag || this.drag.colKey !== col.key) return null;

      const from = Math.min(this.drag.fromMin, this.drag.toMin);
      const to = Math.max(this.drag.fromMin, this.drag.toMin);

      return {
        top: this.topFor(from),
        height: ((to - from) / 15) * this.rowHeight,
        label: this.minutesToLabel(from) + " - " + this.minutesToLabel(to),
      };
    },

    /** The saved blocks in one stylist's column on one day. */
    layoutOffBlocks(staffId, dayKey) {
      const rows = this.timeOffByStaffDay[staffId + "|" + dayKey] || [];

      return rows.map((row) => {
        const start = this.minutesInDay(row.start_at, dayKey);
        // An all-day block ends at midnight the following morning, which clamps to 1440.
        const end = Math.max(this.minutesInDay(row.end_at, dayKey), start + 15);

        return {
          key: "off" + row.id,
          row,
          startMin: start,
          endMin: end,
          top: this.topFor(start),
          height: ((end - start) / 15) * this.rowHeight,
        };
      });
    },

    /** The whole-day block on a stylist's day, if they have one. */
    allDayOffFor(staffId, dayKey) {
      const rows = this.timeOffByStaffDay[staffId + "|" + dayKey] || [];

      return rows.find((row) => row.is_all_day) || null;
    },

    /**
     * What already occupies [$fromMin, $toMin) in a column, or null.
     *
     * Both kinds count. An appointment is somebody's booking and a block is the stylist
     * being away; neither leaves the time free, and the two-option popup would be offering
     * a choice where there is none. Touching at the edge is not a collision: a block that
     * starts at 1:00 leaves 12:45-1:00 free.
     *
     * @returns {{kind: string, text: string}|null}
     */
    occupantOf(col, fromMin, toMin) {
      const appointment = (col.blocks || []).find(
        (block) => block.startMin < toMin && block.endMin > fromMin
      );

      if (appointment) {
        return {
          kind: "appointment",
          text: col.head + " already has " + appointment.ev.time_text + " – " +
            appointment.ev.end_time_text +
            (appointment.ev.customer_name ? " with " + appointment.ev.customer_name : "") + ".",
        };
      }

      const off = (col.offBlocks || []).find(
        (block) => block.startMin < toMin && block.endMin > fromMin
      );

      return off ? { kind: "off", text: this.offTitle(off.row) } : null;
    },

    sayOccupied(occupant) {
      this.message = occupant.text;
      this.snackbar = "danger";
      this.setTimeOutNotification();
    },

    minutesToTime(minutes) {
      const pad = (n) => String(n).padStart(2, "0");
      // 1440 is midnight at the far end of the day; the API reads 00:00 as exactly that.
      const clamped = minutes >= 1440 ? 0 : minutes;

      return pad(Math.floor(clamped / 60)) + ":" + pad(clamped % 60);
    },

    minutesToLabel(minutes) {
      return moment(this.selectedDate + " " + this.minutesToTime(minutes), "YYYY-MM-DD HH:mm")
        .format("h:mm A");
    },

    isPresetOn(preset) {
      if (!this.timeOffForm) return false;

      if (preset.all_day) return this.timeOffForm.is_all_day;

      return (
        !this.timeOffForm.is_all_day &&
        this.timeOffForm.start_time === preset.start &&
        this.timeOffForm.end_time === preset.end
      );
    },

    applyOffPreset(preset) {
      if (preset.all_day) {
        this.timeOffForm.is_all_day = true;
        return;
      }

      this.timeOffForm.is_all_day = false;
      this.timeOffForm.start_time = preset.start;
      this.timeOffForm.end_time = preset.end;
    },

    closeTimeOffForm() {
      this.timeOffForm = null;
    },

    closeSlotChoice() {
      this.slotChoice = null;
    },

    chooseAppointment() {
      // Checked before the popup closes, so the merchant is not left staring at the grid
      // wondering what happened to their click.
      if (!this.requireBranch()) return;

      const choice = this.slotChoice;
      this.slotChoice = null;

      this.openCreateForm(
        Object.assign(
          {
            staff_id: choice.staff_id,
            date: choice.date,
            time: this.minutesToTime(choice.from_min),
          },
          // A dragged stretch sized the appointment; a single click said only when it
          // starts, so that one keeps the usual default length.
          choice.dragged ? { duration_minutes: choice.to_min - choice.from_min } : {}
        )
      );
    },

    chooseDayOff() {
      const choice = this.slotChoice;
      this.slotChoice = null;

      this.timeOffForm = {
        staff_id: choice.staff_id,
        staff_name: choice.staff_name,
        date: choice.date,
        // Whatever was clicked or dragged, to the quarter hour. The presets are one click
        // away for a half or a whole day.
        is_all_day: false,
        start_time: this.minutesToTime(choice.from_min),
        end_time: this.minutesToTime(choice.to_min),
        note: "",
      };
    },

    offTitle(row) {
      const when = row.is_all_day
        ? "all day"
        : row.time_text + " - " + row.end_time_text;

      return (row.staff_name || "Staff") + " is off " + when +
        (row.note ? " — " + row.note : "");
    },

    async submitTimeOff() {
      const form = this.timeOffForm;

      if (!form) return;

      if (!form.is_all_day && (!form.start_time || !form.end_time)) {
        this.message = "Please give the block a start and an end time.";
        this.snackbar = "danger";
        this.setTimeOutNotification();
        return;
      }

      this.showLoader();

      await BookingService.createTimeOff({
        staff_id: form.staff_id,
        off_date: form.date,
        is_all_day: form.is_all_day,
        start_time: form.is_all_day ? null : form.start_time,
        end_time: form.is_all_day ? null : form.end_time,
        note: form.note,
      }).then(
        async (response) => {
          if (response.data.result.isSuccess) {
            this.message = form.staff_name + " is now blocked out on " +
              moment(form.date).format("ddd, D MMM") + ".";
            this.snackbar = "success";
            this.timeOffForm = null;
            await this.loadAppointments();
          } else {
            // The endpoint refuses a block over an appointment somebody has already been
            // given, and says which one — leave the card open so the times can be edited.
            this.message = response.data.result.message;
            this.snackbar = "danger";
          }
          this.setTimeOutNotification();
          this.hideLoader();
        },
        (error) => {
          this.message = String(error);
          this.snackbar = "danger";
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },

    /**
     * SH040 — Delete in the drawer asks first; the answer arrives in
     * confirmDeleteAppointment().
     *
     * The drawer stays open behind the question. Closing it here would mean a merchant who
     * changes their mind has to find and re-open the appointment, and the confirmation is
     * drawn over the top anyway.
     */
    askDeleteAppointment() {
      if (!this.editing || !this.editing.event_id) return;

      this.apptToDelete = this.editing;
    },

    closeDeleteAppointment() {
      this.apptToDelete = null;
    },

    /**
     * Removes the appointment for good: off the calendar, cancelled as a booking, its POS
     * bill withdrawn, and the customer emailed. All of that is the endpoint's work — what
     * matters here is that the email is reported separately from the deletion, because the
     * appointment is gone either way and a merchant whose customer was not told needs to
     * know to phone them.
     */
    async confirmDeleteAppointment() {
      const ev = this.apptToDelete;

      if (!ev) return;

      this.apptToDelete = null;
      this.showLoader();

      await BookingService.deleteCalendarEvent(ev.event_id).then(
        async (response) => {
          if (response.data.result.isSuccess) {
            const d = response.data.data || {};

            if (d.invite_error) {
              this.message =
                "Appointment deleted, but the cancellation email failed: " + d.invite_error;
              this.snackbar = "danger";
            } else if (d.invite_sent) {
              this.message =
                "Appointment deleted. Cancellation email sent to " + d.customer_email + ".";
              this.snackbar = "success";
            } else {
              this.message = "Appointment deleted.";
              this.snackbar = "success";
            }

            this.showCreate = false;
            this.editing = null;
            this.createForm = this.blankCreateForm();
            await this.loadAppointments();
          } else {
            this.message = response.data.result.message;
            this.snackbar = "danger";
          }

          this.setTimeOutNotification();
          this.hideLoader();
        },
        (error) => {
          this.message = String(error);
          this.snackbar = "danger";
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },

    /** The x on a block asks first; the answer arrives in confirmRemoveOff(). */
    removeTimeOff(row) {
      this.offToRemove = row;
    },

    closeRemoveOff() {
      this.offToRemove = null;
    },

    onRemoveOffKeydown(event) {
      if (event.key !== "Escape" && event.key !== "Esc") return;

      if (this.offToRemove) {
        this.closeRemoveOff();
      }

      // SH040 — the delete confirmation earns the same escape hatch, and answers first when
      // both are somehow open: it is the one drawn on top.
      if (this.apptToDelete) {
        this.closeDeleteAppointment();
      }
    },

    async confirmRemoveOff() {
      const row = this.offToRemove;

      if (!row) return;

      this.offToRemove = null;
      this.showLoader();

      await BookingService.deleteTimeOff(row.id).then(
        async (response) => {
          if (response.data.result.isSuccess) {
            this.message = "Block removed.";
            this.snackbar = "success";
            await this.loadAppointments();
          } else {
            this.message = response.data.result.message;
            this.snackbar = "danger";
          }
          this.setTimeOutNotification();
          this.hideLoader();
        },
        (error) => {
          this.message = String(error);
          this.snackbar = "danger";
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },

    /** Day columns are keyed "s<id>", week columns "<id>|<date>". */
    columnStaffId(col) {
      return this.view === "day"
        ? Number(col.key.slice(1))
        : Number(col.key.split("|")[0]);
    },

    /**
     * The appointment a new one would land on top of, or null.
     *
     * Only ever consulted for a date already loaded — outside that window this screen has
     * no appointments to compare against, and a check that silently always passes is worse
     * than no check. The server refuses either way; this only saves the round trip.
     */
    /**
     * @param {string|null} ignoreKey  SH035 — the appointment being edited. It sits in its
     *   own old slot, so without this every edit that keeps the stylist is reported as a
     *   clash with the customer being edited.
     */
    findStaffConflict(staffId, startAt, durationMinutes, ignoreKey = null) {
      const range = this.windowRange;
      const start = moment(startAt, "YYYY-MM-DD HH:mm:ss");

      if (!start.isBetween(range.start, range.end, "day", "[]")) return null;

      const end = start.clone().add(durationMinutes, "minutes");

      return (
        this.appointments.find((ev) => {
          if (ev.key === ignoreKey) return false;
          if (Number(ev.staff_id) !== staffId) return false;

          return (
            moment(ev.start_at, "YYYY-MM-DD HH:mm:ss").isBefore(end) &&
            moment(ev.end_at, "YYYY-MM-DD HH:mm:ss").isAfter(start)
          );
        }) || null
      );
    },

    isStaffHidden(staffId) {
      return this.hiddenStaffIds.includes(staffId);
    },

    toggleStaff(staffId) {
      if (this.isStaffHidden(staffId)) {
        this.hiddenStaffIds = this.hiddenStaffIds.filter((id) => id !== staffId);
      } else {
        this.hiddenStaffIds = this.hiddenStaffIds.concat(staffId);
      }
    },

    showAllStaff() {
      this.hiddenStaffIds = [];
    },

    hideAllStaff() {
      this.hiddenStaffIds = this.staffOptions.map((staff) => staff.staff_id);
    },

    /**
     * Switching sale channel changes who is on the list. A stylist hidden under the old
     * channel and absent from the new one would otherwise keep hiding appointments that
     * nothing in the panel could ever unhide.
     */
    dropStaleHiddenStaff() {
      const known = this.staffOptions.map((staff) => staff.staff_id);
      this.hiddenStaffIds = this.hiddenStaffIds.filter((id) => known.includes(id));
    },

    async submitCreate() {
      const f = this.createForm;
      if (!f.customer_name || !f.service_name || !f.date || !f.time || !f.duration_minutes) {
        this.message = "Customer name, service, date, time and duration are required.";
        this.snackbar = "danger";
        this.setTimeOutNotification();
        return;
      }

      if (!f.staff_id) {
        this.message = "Please pick the stylist this appointment is for.";
        this.snackbar = "danger";
        this.setTimeOutNotification();
        return;
      }

      // SH031 — the endpoint refuses this too, and has to: it is the only guard on the bill.
      // Asking here means the merchant is told before the dialog closes rather than after.
      if (this.sendOrdersToPos && !String(f.customer_phone || "").trim()) {
        this.message =
          "This branch sends its appointments to POS, so the customer's phone number " +
          "is required — it is what opens their bill.";
        this.snackbar = "danger";
        this.setTimeOutNotification();
        return;
      }

      const startAt = f.date + " " + f.time + ":00";
      const clash = this.findStaffConflict(f.staff_id, startAt, f.duration_minutes);

      if (clash) {
        this.message =
          this.staffLabel(f.staff_id) + " already has " + clash.time_text + " – " +
          clash.end_time_text + " with " + (clash.customer_name || "another customer") +
          ". Pick another time or another stylist.";
        this.snackbar = "danger";
        this.setTimeOutNotification();
        return;
      }

      this.showLoader();
      const payload = {
        customer_name: f.customer_name,
        customer_phone: f.customer_phone,
        customer_email: f.customer_email,
        service_name: f.service_name,
        staff_id: f.staff_id,
        staff_name: this.staffLabel(f.staff_id),
        start_at: startAt,
        duration_minutes: f.duration_minutes,
        price: f.price,
        note: f.note,
        branch_id: this.branchId || null,
        // SH031 — the menu items behind the appointment. service_name is the line the
        // calendar shows; these are what POS rings up, and the backend re-reads their
        // prices off the menu rather than trusting the numbers above.
        service_item_id: f.service_item_id,
        service_option_ids: (f.service_options || []).map((option) => option.item_id),
      };

      await BookingService.createCalendarEvent(payload).then(
        async (response) => {
          if (response.data.result.isSuccess) {
            const d = response.data.data;
            // The booking still succeeded if the invite bounced, so say which happened.
            // SH031 — the appointment exists either way; a bill POS would not take is
            // the merchant's to ring up by hand, so it has to be said out loud.
            if (d.pos_error) {
              this.message = d.pos_error;
              this.snackbar = "danger";
            } else if (d.invite_error) {
              this.message = "Appointment created, but the invite email failed: " + d.invite_error;
              this.snackbar = "danger";
            } else if (d.invite_sent) {
              this.message = "Appointment created. Invite sent to " + f.customer_email + ".";
              this.snackbar = "success";
            } else {
              this.message = "Appointment created on Google Calendar.";
              this.snackbar = "success";
            }
            this.showCreate = false;
            this.selectedDate = f.date;
            this.cursor = moment(f.date).startOf("month");
            this.syncMiniCursor();
            this.createForm = this.blankCreateForm();
            await this.loadAppointments();
          } else {
            this.message = response.data.result.message;
            this.snackbar = "danger";
          }
          this.setTimeOutNotification();
          this.hideLoader();
        },
        (error) => {
          this.message = String(error);
          this.snackbar = "danger";
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },

    staffLabel(staffId) {
      const staff = this.staffOptions.find((option) => option.staff_id === staffId);

      return staff ? staff.staff_name : "";
    },

    /**
     * Opens the appointment dialog, prefilled with wherever it was opened from.
     *
     * @param {object} prefill  staff_id / date / time, when a grid cell asked for it
     */
    /**
     * SH041 — an appointment cannot be booked into "all branches".
     *
     * The service, its price and its duration all come from one branch's menu set, so the
     * form has nothing to offer until the merchant says which branch. Refused here rather
     * than opened-then-emptied: a form whose Service list says "no services" reads as a
     * broken menu, not as a question the merchant has not answered yet.
     *
     * @return {boolean} whether the branch is settled
     */
    requireBranch() {
      if (this.branchId) return true;

      this.message = "Please choose a branch on the calendar first — an appointment's service comes from that branch's menu.";
      this.snackbar = "danger";
      this.setTimeOutNotification();

      return false;
    },

    openCreateForm(prefill = {}) {
      if (!this.requireBranch()) return;

      this.editing = null;
      this.createForm = Object.assign(
        this.blankCreateForm(),
        { date: this.selectedDate || moment().format(DATE_KEY) },
        prefill
      );
      this.showCreate = true;
      this.loadServices();
    },

    closeCreateForm() {
      this.showCreate = false;
      this.editing = null;
    },

    /**
     * SH035 — a click on a month-view chip. The day is selected as well as the appointment
     * opened, so closing the drawer leaves the merchant on the day they were looking at
     * rather than back where they started.
     *
     * @param {string} dateKey
     * @param {object} ev
     */
    openFromMonth(dateKey, ev) {
      this.selectDate(dateKey);
      this.openEditForm(ev);
    },

    /**
     * Opens the appointment the merchant clicked, in the same form they created it with.
     *
     * The row is prefilled from what the calendar already holds rather than re-fetched: the
     * whole window is in memory, and a spinner between the click and the drawer would make
     * the calendar feel like it was loading a page.
     *
     * @param {object} ev  an appointment row from the API
     */
    openEditForm(ev) {
      if (!ev || !ev.event_id) {
        // Nothing to write back to. A booking whose Google event never landed is the only
        // way here, and it says so on the row already.
        this.message =
          "This appointment is not on Google Calendar yet, so it cannot be edited here.";
        this.snackbar = "danger";
        this.setTimeOutNotification();
        return;
      }

      this.editing = ev;
      this.createForm = Object.assign(this.blankCreateForm(), {
        customer_name: ev.customer_name || "",
        customer_phone: ev.customer_phone || "",
        customer_email: ev.customer_email || "",
        service_item_id: ev.service_item_id || null,
        service_name: ev.service_name || "",
        // Ids alone, which is all the payload carries back. Names and prices are the
        // menu's job, and the endpoint re-reads them from it on save.
        service_options: (ev.service_option_ids || []).map((id) => ({ item_id: id })),
        staff_id: Number(ev.staff_id) || "",
        date: ev.date,
        time: moment(ev.start_at, "YYYY-MM-DD HH:mm:ss").format("HH:mm"),
        duration_minutes: Number(ev.duration_min) || 45,
        price: ev.total_amount === null || ev.total_amount === undefined
          ? null
          : Number(ev.total_amount),
        note: ev.note || "",
        merchant_memo: ev.merchant_memo || "",
      });

      this.showCreate = true;
      this.loadServices();
    },

    /**
     * Saves the edit. Mirrors submitCreate() field for field — same validation, same clash
     * check, same reading of what came back — because the merchant is looking at the same
     * form and a rule that applied when they typed it must still apply when they fix it.
     */
    async submitEdit() {
      const f = this.createForm;
      const ev = this.editing;

      if (!ev) return;

      if (!f.customer_name || !f.service_name || !f.date || !f.time || !f.duration_minutes) {
        this.message = "Customer name, service, date, time and duration are required.";
        this.snackbar = "danger";
        this.setTimeOutNotification();
        return;
      }

      if (!f.staff_id) {
        this.message = "Please pick the stylist this appointment is for.";
        this.snackbar = "danger";
        this.setTimeOutNotification();
        return;
      }

      if (this.sendOrdersToPos && !String(f.customer_phone || "").trim()) {
        this.message =
          "This branch sends its appointments to POS, so the customer's phone number " +
          "is required — it is what opens their bill.";
        this.snackbar = "danger";
        this.setTimeOutNotification();
        return;
      }

      const startAt = f.date + " " + f.time + ":00";
      const clash = this.findStaffConflict(f.staff_id, startAt, f.duration_minutes, ev.key);

      if (clash) {
        this.message =
          this.staffLabel(f.staff_id) + " already has " + clash.time_text + " – " +
          clash.end_time_text + " with " + (clash.customer_name || "another customer") +
          ". Pick another time or another stylist.";
        this.snackbar = "danger";
        this.setTimeOutNotification();
        return;
      }

      this.showLoader();

      await BookingService.updateCalendarEvent({
        event_id: ev.event_id,
        customer_name: f.customer_name,
        customer_phone: f.customer_phone,
        customer_email: f.customer_email,
        service_name: f.service_name,
        staff_id: f.staff_id,
        staff_name: this.staffLabel(f.staff_id),
        start_at: startAt,
        duration_minutes: f.duration_minutes,
        price: f.price,
        note: f.note,
        merchant_memo: f.merchant_memo,
        branch_id: this.formBranchId || null,
        service_item_id: f.service_item_id,
        service_option_ids: (f.service_options || []).map((option) => option.item_id),
      }).then(
        async (response) => {
          if (response.data.result.isSuccess) {
            const d = response.data.data;

            // The calendar is already rewritten by the time any of these can fail, so each
            // one is a thing the merchant has to know about rather than a failed save.
            if (d.booking_error) {
              this.message = d.booking_error;
              this.snackbar = "danger";
            } else if (d.invite_error) {
              this.message = "Appointment updated, but the new invite failed: " + d.invite_error;
              this.snackbar = "danger";
            } else if (d.invite_sent) {
              this.message = "Appointment updated. Updated invite sent to " + f.customer_email + ".";
              this.snackbar = "success";
            } else {
              this.message = "Appointment updated.";
              this.snackbar = "success";
            }

            this.showCreate = false;
            this.editing = null;
            this.selectedDate = f.date;
            this.cursor = moment(f.date).startOf("month");
            this.syncMiniCursor();
            this.createForm = this.blankCreateForm();
            await this.loadAppointments();
          } else {
            this.message = response.data.result.message;
            this.snackbar = "danger";
          }

          this.setTimeOutNotification();
          this.hideLoader();
        },
        (error) => {
          this.message = String(error);
          this.snackbar = "danger";
          this.setTimeOutNotification();
          this.hideLoader();
        }
      );
    },

    setView(value) {
      if (this.view === value) return;

      const wasMonth = this.view === "month";
      this.view = value;

      // Paging months without picking a day leaves the selection behind. Day and week need
      // one, and quietly snapping back to today's month would lose the merchant's place.
      if (wasMonth && !moment(this.selectedDate).isSame(this.cursor, "month")) {
        this.selectedDate = this.cursor.clone().startOf("month").format(DATE_KEY);
      }

      this.cursor = moment(this.selectedDate).startOf("month");
      this.syncMiniCursor();
      this.loadAppointments();
    },

    shiftRange(delta) {
      if (this.view === "month") {
        this.cursor = this.cursor.clone().add(delta, "month");
      } else {
        const next = moment(this.selectedDate).add(delta, this.view === "day" ? "day" : "week");
        this.selectedDate = next.format(DATE_KEY);
        this.cursor = next.clone().startOf("month");
      }

      this.syncMiniCursor();
      this.loadAppointments();
    },

    shiftMiniMonths(delta) {
      // A picker, not a view: paging it costs no round trip.
      this.miniCursor = this.miniCursor.clone().add(delta, "month");
    },

    /** Keeps the rail showing the month the grid is on, without fighting a manual page. */
    syncMiniCursor() {
      const offset = this.cursor.diff(this.miniCursor, "months");

      if (offset < 0 || offset > 1) {
        this.miniCursor = this.cursor.clone().startOf("month");
      }
    },

    pickDate(key) {
      this.selectedDate = key;

      const month = moment(key).startOf("month");
      const monthChanged = !month.isSame(this.cursor, "month");
      this.cursor = month;
      this.syncMiniCursor();

      // Month view already holds the whole month; day and week are looking at a different
      // window the moment the day changes.
      if (this.view !== "month" || monthChanged) {
        this.loadAppointments();
      }
    },

    goToday() {
      this.cursor = moment().startOf("month");
      this.miniCursor = this.cursor.clone();
      this.selectedDate = moment().format(DATE_KEY);
      this.loadAppointments();
    },

    onChangeBranch(value) {
      this.branchId = value;
      // SH041 — the menu belongs to the branch, so the one held from the branch before is
      // no longer the answer. Dropped rather than refetched: nothing needs it until the
      // merchant opens the form again.
      this.services = [];
      this.servicesBranchId = null;
      this.loadAppointments();
    },

    selectDate(key) {
      this.selectedDate = key;
    },

    /** Joined rather than inlined so a missing field never leaves a dangling separator. */
    apptMeta(ev) {
      return [
        ev.customer_phone,
        ev.customer_email,
        ev.staff_text,
        ev.total_amount !== null && ev.total_amount !== undefined
          ? "$" + Number(ev.total_amount).toFixed(2)
          : null,
        ev.booking_code,
        // The customer booked; only the mirror to Google failed. Say so rather than
        // leaving a row that quietly has no "Open in Google" link.
        ev.calendar_error ? "not on Google Calendar" : null,
      ]
        .filter(Boolean)
        .join("  ·  ");
    },

    chipTitle(ev) {
      return [
        ev.time_text + " – " + ev.end_time_text,
        ev.customer_name,
        ev.service_name,
        ev.staff_text,
      ]
        .filter(Boolean)
        .join("  ·  ");
    },

    formatDateLong(key) {
      return moment(key).format("dddd, D MMMM YYYY");
    },

    showLoader() {
      this.loaderActive = true;
    },

    hideLoader() {
      this.loaderActive = false;
    },

    closeSnackbar() {
      this.snackbar = null;
    },

    setTimeOutNotification() {
      setTimeout(() => {
        this.snackbar = null;
      }, 3000);
    },
  },
};
</script>

<style scoped>
.page-header .container {
  color: #222 !important;
}

.w-100px {
  width: 100px;
}

.s-branch {
  min-width: 340px;
}

.conn-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  padding: 8px 14px;
  border-radius: 8px;
}

.conn-ok {
  background: #eaf6ef;
  color: #1e6b45;
}

.conn-bad {
  background: #fdeceb;
  color: #a5281c;
}

.conn-warn {
  background: #fdf4e3;
  color: #8a6100;
}

.conn-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  flex-shrink: 0;
}

.conn-muted {
  color: #8a8a8e;
  font-weight: 400;
}

.month-nav {
  gap: 6px;
}

.month-label {
  min-width: 160px;
  text-align: center;
  font-weight: 600;
  font-size: 15px;
}

.btn-nav {
  width: 30px;
  height: 30px;
  line-height: 1;
  border: 1px solid #e3e3e3;
  background: #fff;
  border-radius: 6px;
  font-size: 18px;
  color: #444;
}

.btn-plain {
  border: 1px solid #e3e3e3;
  background: #fff;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 13px;
  color: #444;
}

.btn-accent {
  background: #1c1c1e;
  border-color: #1c1c1e;
  color: #fff;
}

/* SH038 — the appointment form is a dialog now; what was the panel's own frame is the
   dialog's, and only the field styling below is still its own. */
.appt-card {
  width: min(720px, calc(100vw - 32px));
  max-height: calc(100vh - 64px);
  overflow-y: auto;
}

.appt-card label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8a8a8e;
  margin-bottom: 4px;
  display: block;
}

.appt-card .form-control {
  border: 1px solid #e3e3e3;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 14px;
  width: 100%;
}

.field-hint {
  font-size: 11px;
  color: #8a8a8e;
  margin-top: 4px;
}

.btn-mini {
  padding: 3px 10px;
  font-size: 12px;
}

.staff-filter {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 10px;
  padding: 14px 16px;
}

.staff-filter-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 13px;
  margin-bottom: 10px;
}

.staff-filter-title {
  font-weight: 600;
  font-size: 14px;
}

.staff-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.staff-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid #e3e3e3;
  border-radius: 999px;
  padding: 5px 12px 5px 10px;
  font-size: 13px;
  color: #444;
  cursor: pointer;
  margin-bottom: 0;
  user-select: none;
}

.staff-chip input {
  margin: 0;
  cursor: pointer;
  accent-color: #c9a227;
}

/* Hidden stylists stay in place and stay legible — the row is a toggle, not a delete. */
.staff-chip.is-off {
  background: #fafafa;
  color: #a0a0a5;
}

.staff-chip.is-off .staff-chip-name {
  text-decoration: line-through;
}

.staff-chip-count {
  font-size: 11px;
  font-weight: 600;
  color: #8a8a8e;
  background: #f2f2f2;
  border-radius: 999px;
  padding: 1px 7px;
}

.staff-empty {
  font-size: 13px;
  color: #8a8a8e;
}

.view-switch {
  display: inline-flex;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.view-switch-btn {
  border: none;
  border-right: 1px solid #ececec;
  background: transparent;
  padding: 6px 16px;
  font-size: 13px;
  color: #444;
}

.view-switch-btn:last-child {
  border-right: none;
}

.view-switch-btn.is-on {
  background: #1c1c1e;
  color: #fff;
}

.cal-layout {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

/* min-width:0 or a wide grid refuses to shrink and pushes the rail off screen. */
.cal-main {
  flex: 1;
  min-width: 0;
}

.cal-rail {
  width: 212px;
  flex-shrink: 0;
}

.mini-cal {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 10px;
  padding: 10px 10px 8px;
  margin-bottom: 12px;
}

.mini-head {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 6px;
}

.mini-title {
  flex: 1;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #444;
}

.mini-nav {
  width: 20px;
  height: 20px;
  line-height: 1;
  border: none;
  background: transparent;
  color: #8a8a8e;
  font-size: 15px;
}

/* Hidden, not removed: dropping it would re-centre the month name between the two cards. */
.mini-nav.is-hidden {
  visibility: hidden;
}

.mini-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 1px;
}

.mini-dow {
  font-size: 9px;
  color: #a0a0a5;
  text-align: center;
  padding-bottom: 2px;
}

.mini-day {
  height: 20px;
  line-height: 20px;
  text-align: center;
  font-size: 10px;
  color: #444;
  border-radius: 4px;
  cursor: pointer;
}

.mini-day.is-outside {
  color: #c8c8cc;
}

.mini-day.in-range {
  background: #f0ebe3;
}

.mini-day.is-today {
  font-weight: 700;
  color: #b8860b;
}

.mini-day.is-selected {
  background: #1c1c1e;
  color: #fff;
}

.rail-today {
  width: 100%;
}

.cal-wrap {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 10px;
  overflow: hidden;
}

/* The grid is one scroll box in both axes, so the stylist header and the hour gutter can
   stay put with plain sticky instead of a second, mirrored element kept in sync by hand. */
.tg-scroll {
  --tg-gutter: 56px;
  max-height: 620px;
  overflow: auto;
}

.tg-inner {
  display: grid;
  position: relative;
}

.tg-corner {
  position: sticky;
  top: var(--tier1-h);
  left: 0;
  z-index: 3;
  background: #fafafa;
  border-bottom: 1px solid #ececec;
  border-right: 1px solid #ececec;
}

/* Week stacks two header tiers, so the stylist row owns the top and the day row parks
   directly beneath it. Day view has one tier and the offset stays zero. */
.tg-inner {
  --tier1-h: 0px;
}

.tg-inner.has-groups {
  --tier1-h: 30px;
}

.tg-grouphead {
  grid-column: span 7;
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: var(--tier1-h);
  padding: 0 8px;
  background: #f4f2ee;
  border-bottom: 1px solid #ececec;
  border-right: 1px solid #e0dcd4;
  font-size: 12px;
  font-weight: 600;
  color: #3a3a3c;
}

.tg-corner-top {
  top: 0;
  z-index: 4;
  height: var(--tier1-h);
}

.tg-colhead {
  position: sticky;
  top: var(--tier1-h);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 8px;
  background: #fafafa;
  border-bottom: 1px solid #ececec;
  border-right: 1px solid #f2f2f2;
  font-size: 12px;
  font-weight: 600;
  color: #444;
}

.tg-colhead-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tg-colhead.is-today {
  background: #fdf8ea;
  color: #8a6100;
}

.tg-colhead-sub {
  font-size: 12px;
  font-weight: 700;
}

.tg-colhead-count {
  font-size: 10px;
  font-weight: 600;
  color: #8a8a8e;
  background: #f0f0f0;
  border-radius: 999px;
  padding: 1px 6px;
}

.tg-gutter {
  position: sticky;
  left: 0;
  z-index: 1;
  background: #fff;
  border-right: 1px solid #ececec;
}

.tg-hour {
  height: calc(var(--row-h) * 4);
  position: relative;
}

.tg-hour-label {
  position: absolute;
  top: -6px;
  right: 8px;
  font-size: 10px;
  color: #8a8a8e;
  background: #fff;
  padding: 0 2px;
}

/* The rows are drawn as a gradient rather than one element each — the week view multiplies
   them by seven days, and the DOM cost is what would make it crawl. SH033 — how many there
   are is the opening hours' business now, so the height comes from --rows. */
.tg-col {
  cursor: cell;
  position: relative;
  height: calc(var(--row-h) * var(--rows));
  border-right: 1px solid #f2f2f2;
  background-image:
    repeating-linear-gradient(
      to bottom,
      #e4e4e4 0,
      #e4e4e4 1px,
      transparent 1px,
      transparent calc(var(--row-h) * 4)
    ),
    repeating-linear-gradient(
      to bottom,
      #f1f1f1 0,
      #f1f1f1 1px,
      transparent 1px,
      transparent var(--row-h)
    );
}

/* Where one stylist's seven days stop. The header groups them; without this the body
   columns run together and a block reads as belonging to the stylist next door. */
.tg-col.is-group-end,
.tg-colhead.is-group-end {
  border-right: 1px solid #d9d4ca;
}

/* SH033 — an hour the sale channel is shut. Hatched rather than flat so it reads as
   "nothing goes here" instead of as a very long appointment, and pointer-events off so the
   column underneath still gets the click that the handler then refuses out loud. */
.tg-shut {
  position: absolute;
  left: 0;
  right: 0;
  z-index: 0;
  pointer-events: none;
  background-color: #f4f4f5;
  background-image: repeating-linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.045) 0,
    rgba(0, 0, 0, 0.045) 1px,
    transparent 1px,
    transparent 7px
  );
}

/* SH045 — one stylist off shift while the salon trades: locked, and drawn as the block a
   day off is so the two read as the same "nothing goes here". The difference is the remove
   button, which this one does not get. Pointer-events off so the column underneath still
   gets the click the handler then refuses out loud. */
.tg-off.is-locked {
  z-index: 0;
  pointer-events: none;
}

.tg-col.is-closed {
  cursor: not-allowed;
}

.tg-colhead-closed {
  font-size: 10px;
  font-weight: 700;
  color: #6b6b70;
  background: #ececed;
  border-radius: 999px;
  padding: 1px 7px;
}

.tg-now {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: #d64545;
  z-index: 2;
}

.tg-block {
  position: absolute;
  overflow: hidden;
  padding: 2px 6px;
  background: #f0ebe3;
  border: 1px solid #e2d7c3;
  border-left: 3px solid #c9a227;
  border-radius: 4px;
  font-size: 11px;
  line-height: 1.25;
  color: #3a3a3c;
  /* SH035 — every appointment opens its own drawer, so it has to look like it can be
     clicked. The grid underneath is a drag surface, which is why only the block changes. */
  cursor: pointer;
}

.tg-block:hover {
  border-color: #c9a227;
  box-shadow: 0 2px 6px rgba(201, 162, 39, 0.28);
}

/* A quarter-hour block is 14px tall: only one line fits, so keep the name, drop the rest. */
.is-short {
  padding-top: 0;
  padding-bottom: 0;
  font-size: 10px;
  line-height: 12px;
}

.is-short .tg-block-time,
.is-short .tg-block-service {
  display: none;
}

.is-narrow {
  padding-left: 4px;
  padding-right: 3px;
  font-size: 10px;
}

.is-narrow .tg-block-time,
.is-narrow .tg-block-service {
  display: none;
}

.tg-block-time {
  font-weight: 600;
  white-space: nowrap;
}

.tg-block-name {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tg-block-service {
  color: #6a6a6e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.grid-empty {
  font-size: 13px;
  color: #8a8a8e;
  padding: 28px 18px;
}

/* The rail is a companion to the grid, not a second column, so it goes under it rather
   than squeezing the grid to nothing on a laptop screen. */
@media (max-width: 1199px) {
  .cal-layout {
    flex-direction: column;
  }

  .cal-rail {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 12px;
  }

  .mini-cal {
    flex: 1 1 200px;
    margin-bottom: 0;
  }

  .rail-today {
    width: auto;
  }
}

.cal-head,
.cal-body {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
}

.cal-head-cell {
  padding: 10px 8px;
  font-size: 11px;
  letter-spacing: 0.08em;
  color: #8a8a8e;
  text-align: center;
  background: #fafafa;
  border-bottom: 1px solid #ececec;
}

.cal-cell {
  min-height: 108px;
  border-right: 1px solid #f2f2f2;
  border-bottom: 1px solid #f2f2f2;
  padding: 6px;
  cursor: pointer;
}

.cal-cell:nth-child(7n) {
  border-right: none;
}

.cal-cell.is-outside {
  background: #fbfbfb;
}

.cal-cell.is-outside .cal-daynum {
  color: #c8c8cc;
}

.cal-cell.is-today .cal-daynum {
  background: #1c1c1e;
  color: #fff;
}

.cal-cell.is-selected {
  box-shadow: inset 0 0 0 2px #c9a227;
}

.cal-daynum {
  font-size: 12px;
  font-weight: 600;
  color: #444;
  width: 22px;
  height: 22px;
  line-height: 22px;
  text-align: center;
  border-radius: 50%;
  margin-bottom: 4px;
}

.cal-chip {
  font-size: 11px;
  background: #f0ebe3;
  border-left: 3px solid #c9a227;
  border-radius: 3px;
  padding: 2px 5px;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cal-chip.is-clickable {
  cursor: pointer;
}

.cal-chip.is-clickable:hover {
  background: #e8e0d2;
}

.cal-chip-time {
  font-weight: 600;
  margin-right: 4px;
}

.cal-more {
  font-size: 10px;
  color: #8a8a8e;
  padding-left: 5px;
}

.day-panel {
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 10px;
  padding: 16px 18px;
}

.day-panel-head {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 10px;
}

.day-empty {
  font-size: 13px;
  color: #8a8a8e;
  padding: 12px 0;
}

.appt-row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 12px 0;
  border-top: 1px solid #f2f2f2;
}

.appt-row.is-clickable {
  cursor: pointer;
}

.appt-row.is-clickable:hover {
  background: #faf8f4;
}

.appt-time {
  font-size: 13px;
  font-weight: 600;
  min-width: 150px;
  white-space: nowrap;
}

.appt-main {
  flex: 1;
}

.appt-name {
  font-size: 14px;
  font-weight: 600;
}

.appt-service {
  font-weight: 400;
  color: #555;
}

.appt-meta {
  font-size: 12px;
  color: #8a8a8e;
  margin-top: 2px;
}

.appt-link {
  font-size: 12px;
  color: #b8860b;
  white-space: nowrap;
}

/* ------------------------------------------------------------------ SH038
   A stylist's absence. Deliberately unlike an appointment: no gold, no name, and hatched
   rather than filled, so a glance down a column tells a booked hour from a blocked one. */
.conn-hint {
  background: #f4f2ee;
  color: #5a5a5e;
}

.tg-col.is-blocking {
  cursor: crosshair;
}

.tg-off {
  position: absolute;
  left: 0;
  right: 0;
  overflow: hidden;
  padding: 1px 6px;
  border: 1px solid #d8d8dc;
  border-left: 3px solid #9a9aa0;
  border-radius: 4px;
  background-color: #f3f3f5;
  background-image: repeating-linear-gradient(
    135deg,
    rgba(154, 154, 160, 0.22) 0,
    rgba(154, 154, 160, 0.22) 2px,
    transparent 2px,
    transparent 7px
  );
  font-size: 10px;
  line-height: 12px;
  color: #5a5a5e;
  cursor: default;
}

.tg-off-label {
  font-weight: 600;
  white-space: nowrap;
}

.tg-off-x {
  position: absolute;
  top: 0;
  right: 2px;
  border: 0;
  background: transparent;
  color: #7a7a80;
  font-size: 14px;
  line-height: 12px;
  padding: 0 3px;
}

/* The band under the cursor mid-drag: the same shape, without the border that would read
   as something already saved. */
.tg-off.is-preview {
  border-style: dashed;
  border-left-style: solid;
  opacity: 0.85;
  pointer-events: none;
}

.tg-colhead-off {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: 6px;
  padding: 0 4px 0 6px;
  border-radius: 9px;
  background: #ececef;
  color: #5a5a5e;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.tg-off-x-head {
  position: static;
  font-size: 13px;
  line-height: 1;
}

.cal-chip-off {
  background: #f3f3f5;
  border-left-color: #9a9aa0;
  color: #5a5a5e;
}

.choice-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Two answers to one click, so neither is the quiet one: same size, same weight, and the
   subtitle says what each does rather than leaving the merchant to guess. */
.choice {
  display: flex;
  flex-direction: column;
  gap: 2px;
  text-align: left;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  background: #fff;
  padding: 12px 14px;
}

.choice:hover {
  border-color: #1c1c1e;
  background: #fafafa;
}

.choice-title {
  font-size: 14px;
  font-weight: 600;
  color: #1c1c1e;
}

.choice-sub {
  font-size: 12px;
  color: #8a8a8e;
}

/* SH038 — one add-on group and its options. Deliberately plain rows: the merchant is
   reading them out to a customer on the phone, not browsing a menu. */
.addon-group {
  margin-bottom: 14px;
}

.addon-group-head {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 6px;
}

.addon-group-name {
  font-size: 13px;
  font-weight: 600;
  color: #1c1c1e;
}

.addon-group-rule {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8a8a8e;
}

.addon-option {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e3e3e3;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 6px;
  font-size: 13px;
  color: #1c1c1e;
  background: #fff;
}

.addon-option.is-on {
  border-color: #1c1c1e;
  background: #fafafa;
}

.addon-option-name {
  flex: 1;
}

.addon-option-meta {
  font-size: 11px;
  color: #8a8a8e;
}

.addon-option-price {
  font-size: 12px;
  font-weight: 600;
  color: #1c1c1e;
}

.off-overlay {
  position: fixed;
  inset: 0;
  background: rgba(28, 28, 30, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

/* The add-ons open on top of the appointment form, which is an overlay itself: without
   this it renders behind the form it was opened from and nothing in it can be clicked. */
.off-overlay-top {
  z-index: 1060;
}

/* SH035 — the same card, docked. Editing an appointment is a side errand off the grid the
   merchant is reading, so the form takes the right edge and leaves the calendar visible,
   rather than covering the middle of it the way a new appointment does. */
.off-overlay.is-drawer {
  align-items: stretch;
  justify-content: flex-end;
}

.off-overlay.is-drawer .off-card {
  /* Wide enough that the two-column layout the create form uses still fits: a drawer that
     stacked every field would be the same form asking for four more scrolls. */
  width: min(620px, 100vw);
  max-height: 100vh;
  border-radius: 0;
  overflow-y: auto;
  padding: 20px;
  animation: drawer-in 0.18s ease-out;
}

@keyframes drawer-in {
  from {
    transform: translateX(24px);
    opacity: 0.4;
  }
}

.off-card {
  width: min(420px, calc(100vw - 32px));
  background: #fff;
  border-radius: 12px;
  padding: 18px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
}

.off-card-head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}

.off-card-title {
  font-size: 15px;
  font-weight: 600;
  color: #1c1c1e;
}

.off-card-sub {
  font-size: 13px;
  color: #8a8a8e;
}

.off-card-x {
  margin-left: auto;
  border: 0;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: #8a8a8e;
}

.off-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.off-times {
  display: flex;
  gap: 12px;
}

.off-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #8a8a8e;
  margin-bottom: 14px;
}

.off-field-wide {
  width: 100%;
}

.off-input {
  border: 1px solid #e3e3e3;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 13px;
  color: #1c1c1e;
  width: 100%;
}

/* The portal is a light UI throughout, but a field with no background of its own is
   painted by the browser, which follows the viewer's system theme: on a machine in dark
   mode these came out near-black inside a white dialog. color-scheme pins the parts the
   page cannot reach either — the time field's own clock button and its picker. */
.off-input,
.appt-card .form-control {
  background-color: #fff;
  color: #1c1c1e;
  color-scheme: light;
}

.off-input::placeholder,
.appt-card .form-control::placeholder {
  color: #a5a5aa;
}

.off-card-foot {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

/* SH039 — the block being removed, quoted back the way it reads on the grid. */
.off-remove-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border: 1px solid #e3e3e3;
  border-left: 3px solid #d64545;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 12px;
}

.off-remove-when {
  font-size: 13px;
  font-weight: 600;
  color: #1c1c1e;
}

.off-remove-note {
  font-size: 12px;
  color: #8a8a8e;
}

.off-remove-text {
  font-size: 13px;
  color: #8a8a8e;
  margin-bottom: 16px;
}

.btn-danger-plain {
  background: #d64545;
  border-color: #d64545;
  color: #fff;
}
</style>
